import React, { useState, useEffect, useRef, useContext, createContext } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Eye, Brain, MessageCircle, Award, LogOut, Plus, Camera, Play, Square, Send, TrendingUp, User, Users, Stethoscope, BarChart3, FileDown, History, MessageSquarePlus, ListVideo, Share2, Copy, Check, BookOpen, ArrowRight, X, RefreshCw, CheckCircle2, ClipboardList, Bell, BellOff, Trash2, BarChart2, Printer, AlertTriangle, Activity, Target, Zap, Shield, TrendingDown, Briefcase, Sparkles, ChevronLeft, AlertCircle, Save, CreditCard } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import { QRCodeSVG } from "qrcode.react";
import { buildPremiumHTML, openPrintWindow, exportPremiumPDF, printPremium, downloadPremium, printCurriculumGoal, downloadCurriculumGoal, printGoalCertificate, downloadGoalCertificate } from "./PremiumDoc";
import { GamesLibrary, GamePlayer, ChildGamesProgress } from "./Games";
import { CasesList, CaseDetail } from "./MultiDisciplinary";
import { EvaluationDetail } from "./VideoEvaluation";
import { PricingPage, CheckoutSuccess, CheckoutCancelled, Billing, AdminPricingDashboard } from "./Payments"; // legacy (unmounted)
import { AdminPanel } from "./AdminPanel";
import { TeamManagementPage } from "./TeamManagement";
import { LanguageProvider, useT, LanguageToggle } from "./i18n";
import { A11yProvider } from "./Accessibility";

axios.defaults.withCredentials = true;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token interceptor: send Bearer token from localStorage if present (fallback when cookies blocked)
axios.interceptors.request.use(cfg => {
  const t = localStorage.getItem("autismai_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const saveToken = (t) => { if (t) localStorage.setItem("autismai_token", t); };
const clearToken = () => localStorage.removeItem("autismai_token");

// ===== Premium Certificate (Gold-bordered, Inclusion+AI official) =====
function buildCertOpts(cert) {
  const body = `
    <div style="font-size:14px;color:#4A5553;letter-spacing:1px;margin-bottom:8px">CERTIFICATE OF ACHIEVEMENT</div>
    <div class="cert-title">شهادة إنجاز رسمية</div>
    <div class="cert-sub">Inclusion+AI · منصة الدمج التعليمي الذكي</div>
    <div style="font-size:14px;color:#4A5553">تشهد منصة Inclusion+AI بأنّ</div>
    <div class="cert-recipient">${cert.child_name || ""}</div>
    <div class="cert-body">
      قد أتمّ بنجاح هدف التدخل التأهيلي ضمن خطته الفردية،<br/>
      وحقّق المعايير المعتمدة وفقاً للممارسات العلمية في الدمج التعليمي،<br/>
      وذلك بشهادة الفريق المتخصص والذكاء الاصطناعي الإكلينيكي للمنصة.
    </div>
    <div class="cert-achievement">«${cert.goal_title || ""}»</div>
    <div style="margin-top:18px;font-size:14px;color:#1A201E">نسبة الإتقان المُحقّقة: <strong style="color:#2F5233;font-size:18px">${cert.success_rate || cert.mastery_score || 0}%</strong></div>
    <div style="margin-top:30px;display:grid;grid-template-columns:1fr 1fr;gap:32px;max-width:520px;margin-left:auto;margin-right:auto">
      <div style="text-align:center;border-top:1px solid #2F5233;padding-top:8px">
        <div style="font-size:11px;color:#2F5233;font-weight:700">المتخصص المعتمد</div>
        <div style="font-size:10px;color:#6b6b6b;margin-top:2px">${cert.therapist || "—"}</div>
      </div>
      <div style="text-align:center;border-top:1px solid #2F5233;padding-top:8px">
        <div style="font-size:11px;color:#2F5233;font-weight:700">رقم الشهادة</div>
        <div style="font-size:10px;color:#6b6b6b;margin-top:2px;font-family:monospace">${cert.certificate_id || "—"}</div>
      </div>
    </div>
  `;
  return { title: "شهادة إنجاز", docType: "شهادة رسمية", body, variant: "certificate", refPrefix: "CERT", showSeal: false };
}
const printCertificate = (cert) => printPremium(buildCertOpts(cert));
const downloadCertificate = (cert) => downloadPremium(buildCertOpts(cert), `Certificate-${cert.child_name || "achievement"}.pdf`);

// ===== Premium Screening Report =====
function buildScreeningOpts(report) {
  const riskColor = report.risk_level === "مرتفع" ? "pill-rose" : report.risk_level === "متوسط" ? "pill-amber" : "pill-emerald";
  const body = `
    <div class="section">
      <h3>المؤشرات الرئيسية</h3>
      <div class="grid-3">
        <div class="info-row" style="flex-direction:column"><span class="label">النقاط الإجمالية</span><span class="value" style="font-size:28px;color:#2F5233;font-weight:800">${report.overall_score || 0}/100</span></div>
        <div class="info-row" style="flex-direction:column"><span class="label">مستوى الخطر</span><span class="value"><span class="pill ${riskColor}">${report.risk_level || "—"}</span></span></div>
        <div class="info-row" style="flex-direction:column"><span class="label">عدد الملاحظات</span><span class="value" style="font-size:28px;color:#2F5233;font-weight:800">${(report.observations || []).length}</span></div>
      </div>
    </div>
    <div class="section">
      <h3>الملاحظات السريرية</h3>
      <ul>${(report.observations || []).map(o => `<li>${o}</li>`).join("")}</ul>
    </div>
    <div class="section">
      <h3>التوصيات</h3>
      <ul>${(report.recommendations || []).map(o => `<li>${o}</li>`).join("")}</ul>
    </div>
    ${report.disclaimer ? `<div class="quote">${report.disclaimer}</div>` : ""}
  `;
  return {
    title: "تقرير الفحص المبدئي السريري",
    subtitle: "Initial Clinical Screening Report",
    docType: "تقرير سريري رسمي",
    subjectName: report.child_name || "—",
    subjectMeta: { "تاريخ الإصدار": new Date().toLocaleString("ar-EG"), "المعد": report.therapist || "النظام السريري الذكي" },
    body,
    signatures: [{ role: "المعد الإكلينيكي" }, { role: "مدير المنصة" }, { role: "ولي الأمر / المعتمد" }],
    refPrefix: "SCR",
  };
}
const printScreeningReport = (r) => printPremium(buildScreeningOpts(r));
const downloadScreeningReport = (r) => downloadPremium(buildScreeningOpts(r), `Screening-Report-${Date.now()}.pdf`);

// ===== Premium Adapted Plan / ABA Plan =====
function buildPlanOpts(child, plan) {
  const goalsHTML = (plan.goals || []).map(g => `
    <tr>
      <td><strong>${g.title || ""}</strong>${g.success_criteria?`<div style="font-size:11px;color:#6b6b6b">${g.success_criteria}</div>`:""}</td>
      <td>${g.domain || ""}</td>
      <td>${g.duration_weeks || 0} أسبوع</td>
      <td><span class="pill ${(g.progress||0)>=80?"pill-emerald":(g.progress||0)>=40?"pill-amber":"pill-rose"}">${g.progress||0}%</span></td>
    </tr>`).join("");
  const list = (arr) => (arr || []).map(x => `<li>${x}</li>`).join("");
  const body = `
    <div class="section">
      <h3>ملخص الخطة</h3>
      <div class="quote">${plan.summary || "—"}</div>
    </div>
    <div class="grid-2">
      <div class="section"><h3>نقاط القوة</h3><ul>${list(plan.strengths)}</ul></div>
      <div class="section"><h3>التحديات</h3><ul>${list(plan.challenges)}</ul></div>
    </div>
    <div class="section">
      <h3>الأهداف التأهيلية (SMART)</h3>
      <table class="report">
        <thead><tr><th>الهدف ومعيار النجاح</th><th>المجال</th><th>المدة</th><th>التقدم</th></tr></thead>
        <tbody>${goalsHTML}</tbody>
      </table>
    </div>
    ${plan.parent_tips ? `<div class="section"><h3>نصائح لولي الأمر</h3><ul>${list(plan.parent_tips)}</ul></div>` : ""}
  `;
  return {
    title: "الخطة التأهيلية الفردية (IEP)",
    subtitle: "Individualized Education / Intervention Plan",
    docType: "خطة فردية معتمدة",
    subjectName: child.name,
    subjectMeta: { "العمر": `${child.age} سنة`, "النوع": child.gender, "عدد الأهداف": (plan.goals || []).length },
    body,
    signatures: [{ role: "المنسق المسؤول" }, { role: "المتخصص المعتمد" }, { role: "ولي الأمر" }],
    refPrefix: "IEP",
  };
}
const printPlan = (child, plan) => printPremium(buildPlanOpts(child, plan));
const downloadPlan = (child, plan) => downloadPremium(buildPlanOpts(child, plan), `IEP-${child.name}.pdf`);



const displayGender = (g, lang) => {
  if (!g) return "";
  const isMale = g === "male" || g === "ذكر" || (typeof g === "string" && g.toLowerCase().startsWith("m"));
  const isFemale = g === "female" || g === "أنثى" || (typeof g === "string" && g.toLowerCase().startsWith("f"));
  if (isMale)   return lang === "ar" ? "ذكر"  : "Male";
  if (isFemale) return lang === "ar" ? "أنثى" : "Female";
  return g;
};

const fmtDate = (iso, lang = "ar") => { try { return new Date(iso).toLocaleString(lang === "en" ? "en-US" : "ar", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

// ============= AUTH CONTEXT =============
const AuthCtx = createContext();
const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/auth/me`).then(r => setUser(r.data)).catch(() => setUser(false)).finally(() => setLoading(false));
  }, []);
  return <AuthCtx.Provider value={{ user, setUser, loading }}>{children}</AuthCtx.Provider>;
}

const fmtErr = (d) => {
  if (!d) return "حدث خطأ";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || JSON.stringify(d);
};

// ============= SHARED UI =============
const Btn = ({ children, variant = "primary", ...p }) => {
  const styles = {
    primary: "bg-[#2F5233] hover:bg-[#1E3622] text-white",
    secondary: "bg-[#769FCD] hover:bg-[#527598] text-white",
    outline: "bg-white hover:bg-[#F0EFEB] text-[#1A201E] border border-[#E5E2DB]",
    ghost: "hover:bg-[#F0EFEB] text-[#4A5553]",
  };
  return <button {...p} className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 ${styles[variant]} ${p.className||""}`}>{children}</button>;
};
const Input = (p) => <input {...p} className={`w-full px-4 py-2.5 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 focus:border-[#2F5233] outline-none transition text-[#1A201E] ${p.className||""}`}/>;
const Card = ({ children, className="", ...rest }) => <div className={`bg-white rounded-xl border border-[#E5E2DB] p-6 shadow-sm ${className}`} {...rest}>{children}</div>;

// ============= AUTH PAGES =============
function Login() {
  const nav = useNavigate(); const { setUser } = useAuth();
  const { t, dir } = useT();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const doLogin = async (email, password) => {
    setLoading(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      saveToken(data.token); setUser(data); nav("/");
    } catch (er) {
      let msg = "";
      if (!er.response) {
        msg = t("login.err_network");
      } else if (er.response.status === 401) {
        msg = t("login.err_invalid");
      } else if (er.response.status >= 500) {
        msg = t("login.err_server");
      } else {
        msg = fmtErr(er.response.data?.detail) || `Error (${er.response.status})`;
      }
      setErr(msg);
      console.error("Login error:", er);
    } finally {
      setLoading(false);
    }
  };
  const submit = (ev) => { ev.preventDefault(); doLogin(f.email, f.password); };
  const demoLogin = (email) => doLogin(email, "Demo@1234");
  const demos = [
    { key: "head",       email: "demo.head@inclusion-ai.app",       label: t("login.demo_head"),       desc: t("login.demo_head_desc"),       icon: Users,       color: "#2F5233" },
    { key: "specialist", email: "demo.specialist@inclusion-ai.app", label: t("login.demo_specialist"), desc: t("login.demo_specialist_desc"), icon: Stethoscope, color: "#769FCD" },
    { key: "parent",     email: "demo.parent@inclusion-ai.app",     label: t("login.demo_parent"),     desc: t("login.demo_parent_desc"),     icon: User,        color: "#E8A317" },
  ];
  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden grain" dir={dir} style={{background:"linear-gradient(135deg,#F9F8F6 0%,#F0EFEB 100%)"}}>
      <Card className="w-full max-w-md fade-up relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3"><Brain className="text-[#2F5233]" size={32}/><h1 className="text-3xl font-bold text-[#2F5233] font-heading">Inclusion+AI</h1></div>
          <LanguageToggle testid="login-lang-toggle"/>
        </div>
        <h2 className="text-xl font-semibold mb-2">{t("login.title")}</h2>
        <p className="text-[#4A5553] text-sm mb-6">{t("brand.tagline")}</p>

        {/* Demo accounts quick-login */}
        <div className="mb-5 p-3 rounded-xl bg-gradient-to-l from-[#2F5233]/5 via-[#769FCD]/5 to-transparent border border-[#E5E2DB]" data-testid="demo-accounts-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-[#2F5233] inline-flex items-center gap-1"><Sparkles size={14}/> {t("login.demo_title")}</p>
            <span className="text-[10px] text-[#4A5553]">{t("login.demo_subtitle")}</span>
          </div>
          <div className="space-y-2">
            {demos.map(d => {
              const Icon = d.icon;
              return (
                <button
                  type="button"
                  key={d.key}
                  onClick={() => demoLogin(d.email)}
                  disabled={loading}
                  data-testid={`demo-login-${d.key}`}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white hover:bg-[#F9F8F6] border border-[#E5E2DB] hover:border-[#2F5233]/40 transition text-start disabled:opacity-50"
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${d.color}18`, color: d.color }}>
                    <Icon size={18}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A201E]">{d.label}</p>
                    <p className="text-[10px] text-[#4A5553] truncate">{d.desc}</p>
                  </div>
                  <ArrowRight size={14} className={`text-[#4A5553] flex-shrink-0 ${dir === "rtl" ? "rotate-180" : ""}`}/>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-[#4A5553] text-center mt-2">{t("login.demo_creds_label")} <code className="bg-white px-1 rounded">Demo@1234</code></p>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 h-px bg-[#E5E2DB]"/>
          <span className="text-[10px] text-[#4A5553]">{t("login.demo_or_real")}</span>
          <div className="flex-1 h-px bg-[#E5E2DB]"/>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Input data-testid="login-email" type="email" placeholder={t("common.email")} value={f.email} onChange={e=>setF({...f,email:e.target.value})} required/>
          <Input data-testid="login-password" type="password" placeholder={t("common.password")} value={f.password} onChange={e=>setF({...f,password:e.target.value})} required/>
          {err && <p data-testid="login-error" className="text-red-600 text-sm">{err}</p>}
          <Btn data-testid="login-submit" type="submit" disabled={loading} className="w-full">{loading?"...":t("login.submit")}</Btn>
        </form>
        <p className="text-center text-sm mt-4 text-[#4A5553]">{t("login.no_account")} <Link to="/register" data-testid="to-register" className="text-[#2F5233] font-medium hover:underline">{t("login.register_now")}</Link></p>
        <div className="mt-3 pt-3 border-t border-[#E5E2DB] text-center">
          <Link to="/access" data-testid="to-parent-access" className="inline-flex items-center gap-1 text-sm text-[#769FCD] font-medium hover:underline">
            {t("login.parent_access")}
          </Link>
        </div>
      </Card>
    </div>
  );
}

// ===== Parent Access by Code =====
function ParentAccess() {
  const nav = useNavigate();
  const { t, dir, lang } = useT();
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const urlCode = new URLSearchParams(window.location.search).get("code");
    if (urlCode) setCode(urlCode.toUpperCase().replace(/[^A-Z0-9]/g, ""));
  }, []);
  const submit = async (ev) => {
    ev.preventDefault(); setLoading(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/parent-access/redeem`, { code: code.trim().toUpperCase() });
      saveToken(data.token);
      nav("/access/view");
    } catch (er) {
      if (!er.response) setErr(t("login.err_network"));
      else if (er.response.status === 404) setErr(t("access.invalid_code"));
      else setErr(fmtErr(er.response.data?.detail) || `Error (${er.response.status})`);
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-6" dir={dir} style={{background:"linear-gradient(135deg,#F9F8F6,#F0EFEB)"}}>
      <Card className="w-full max-w-md fade-up">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3"><Brain className="text-[#2F5233]" size={32}/><h1 className="text-3xl font-bold text-[#2F5233] font-heading">Inclusion+AI</h1></div>
          <LanguageToggle testid="access-lang-toggle"/>
        </div>
        <h2 className="text-xl font-semibold mb-2">{lang === "ar" ? "ولي أمر — دخول بكود الطالب" : "Parent — sign in with student code"}</h2>
        <p className="text-sm text-[#4A5553] mb-5">{t("access.subtitle")}</p>
        <form onSubmit={submit} className="space-y-4">
          <Input data-testid="access-code" placeholder={t("access.placeholder")} value={code} maxLength={12}
            onChange={e=>setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
            className="text-center text-2xl font-mono tracking-widest" required/>
          {err && <p data-testid="access-error" className="text-red-600 text-sm">{err}</p>}
          <Btn data-testid="access-submit" type="submit" disabled={loading || code.length < 6} className="w-full">{loading?"...":t("access.submit")}</Btn>
        </form>
        <div className="text-center mt-4 text-sm">
          <Link to="/login" className="text-[#4A5553] hover:text-[#2F5233]">{t("access.back_login")}</Link>
        </div>
      </Card>
    </div>
  );
}

// ===== Parent focused view (no sidebar) =====
function ParentView() {
  const nav = useNavigate();
  const [child, setChild] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [goals, setGoals] = useState([]);
  const [resourcesByGoal, setResourcesByGoal] = useState({});
  const [error, setError] = useState("");
  const [expandedGoal, setExpandedGoal] = useState(null);
  useEffect(() => {
    axios.get(`${API}/parent-access/case-summary`)
      .then(r => {
        setChild(r.data.child);
        setCaseData(r.data.case);
        setGoals(r.data.goals || []);
        setResourcesByGoal(r.data.resources_by_goal || {});
      })
      .catch(er => {
        if (er.response?.status === 401 || er.response?.status === 403) nav("/access");
        else setError("تعذّر تحميل بيانات الطفل");
      });
  }, [nav]);
  const logout = () => { clearToken(); nav("/access"); };

  if (error) return <div className="min-h-screen flex items-center justify-center p-6"><Card><p>{error}</p>
    <Link to="/access" className="text-sm text-[#2F5233]">المحاولة بكود آخر</Link></Card></div>;
  if (!child) return <div className="min-h-screen flex items-center justify-center text-[#4A5553]">جاري التحميل...</div>;

  const domainLbl = { academic:"أكاديمي", communication:"تواصل", behavior:"سلوكي", sensory_regulation:"حسي/تنظيم ذاتي", motor_participation:"حركي/مشاركة", social_participation:"اجتماعي" };

  return (
    <div className="min-h-screen p-6 md:p-10" dir="rtl" style={{background:"#F9F8F6"}}>
      <div className="max-w-4xl mx-auto space-y-6 fade-up" data-testid="parent-view">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="flex items-center gap-3"><Brain className="text-[#2F5233]" size={28}/><span className="text-2xl font-bold text-[#2F5233] font-heading">Inclusion+AI</span></div>
          <Btn variant="outline" onClick={logout} data-testid="parent-logout"><LogOut size={14} className="inline ms-1"/>خروج</Btn>
        </div>
        <Card>
          <h1 className="text-3xl font-bold font-heading">{child.name}</h1>
          <p className="text-sm text-[#4A5553] mt-1">{child.age} سنة · {child.gender === "male" ? "ذكر" : child.gender === "female" ? "أنثى" : ""}</p>
          {caseData && <p className="text-xs text-[#769FCD] mt-2 flex items-center gap-1">حالة دمج نشطة · {caseData.grade_class || "—"}</p>}
        </Card>
        {goals.length === 0 ? <Card><p className="text-center text-[#4A5553] py-6">لم يتم إعداد خطة بعد. تواصل مع قسم الدمج.</p></Card> :
          <Card>
            <h2 className="text-xl font-semibold mb-3 font-heading flex items-center gap-2">أهداف طفلك ({goals.length})</h2>
            <p className="text-xs text-[#4A5553] mb-4">انقر على أي هدف لرؤية الأنشطة والقصص التي يمكنك تطبيقها معه في البيت.</p>
            <div className="space-y-2">{goals.map(g => {
              const resources = resourcesByGoal[g.id] || [];
              const isOpen = expandedGoal === g.id;
              return <div key={g.id} className="border border-[#E5E2DB] rounded-lg bg-white" data-testid={`parent-goal-${g.id}`}>
                <button onClick={()=>setExpandedGoal(isOpen?null:g.id)} className="w-full p-3 flex justify-between items-start gap-2 text-right hover:bg-[#F9F8F6] rounded-lg transition">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{g.title}</p>
                    <p className="text-xs text-[#4A5553] mt-0.5">{domainLbl[g.domain] || g.domain}{g.criteria?` · ${g.criteria}`:""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {resources.length > 0 && <span className="text-xs px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full">{resources.length} نشاط</span>}
                    <span className="text-xs text-[#4A5553]">{isOpen?"إخفاء":"عرض"}</span>
                  </div>
                </button>
                {isOpen && <div className="px-3 pb-3 border-t border-[#E5E2DB] pt-2">
                  {resources.length === 0 ? <p className="text-xs text-[#4A5553] py-3 text-center">لم تُولَّد أنشطة منزلية لهذا الهدف بعد.</p>
                    : <div className="space-y-2">{resources.map(r => <ParentResourceItem key={r.id} r={r}/>)}</div>}
                </div>}
              </div>;
            })}</div>
          </Card>}
        <p className="text-center text-xs text-[#4A5553]">— عرض ولي الأمر · القراءة فقط —</p>
      </div>
    </div>
  );
}

function ParentResourceItem({ r }) {
  const c = r.content || {};
  const kindLabel = { home_activity: "نشاط منزلي", story: "قصة قصيرة", flashcards: "بطاقات تعليمية" }[r.kind] || "مورد";
  return <div className="p-3 bg-rose-50 rounded border border-rose-200">
    <p className="text-xs font-semibold text-rose-700">{kindLabel}: {c.title || c.theme || c.name || "—"}</p>
    {r.kind === "home_activity" && <div className="mt-2 text-sm space-y-1.5">
      {c.family_friendly_intro && <p className="text-[#1A201E]">{c.family_friendly_intro}</p>}
      {c.needed_items?.length > 0 && <p className="text-xs"><strong>ستحتاج:</strong> {c.needed_items.join("، ")}</p>}
      {c.steps_for_parent?.length > 0 && <div><strong className="text-xs">خطواتك:</strong><ul className="list-disc ps-5 text-xs mt-1">{c.steps_for_parent.map((s,i)=><li key={`sp-${i}`}>{s}</li>)}</ul></div>}
      {c.talking_points?.length > 0 && <div><strong className="text-xs">قل لطفلك:</strong><ul className="list-disc ps-5 text-xs mt-1 italic">{c.talking_points.map((s,i)=><li key={`tp-${i}`}>«{s}»</li>)}</ul></div>}
      {c.celebrate && <p className="text-xs text-emerald-700 mt-1">✨ {c.celebrate}</p>}
      {c.frequency && <p className="text-[10px] text-[#4A5553] mt-1">التكرار: {c.frequency}</p>}
    </div>}
    {r.kind === "story" && <div className="mt-2 text-sm space-y-1">
      {c.story && <p className="whitespace-pre-wrap text-[#1A201E]">{c.story}</p>}
      {c.moral && <p className="text-xs text-emerald-700 mt-1"><strong>الفكرة:</strong> {c.moral}</p>}
      {c.parent_tip && <p className="text-xs bg-white p-2 rounded mt-1"><strong>نصيحة:</strong> {c.parent_tip}</p>}
    </div>}
    {r.kind === "flashcards" && c.cards?.length > 0 && <div className="grid grid-cols-2 gap-1.5 mt-2">{c.cards.slice(0,6).map((card, i)=><div key={`pc-${i}`} className="p-1.5 bg-white rounded text-xs text-center"><strong className="text-purple-700">{card.front}</strong><p className="text-[10px] text-[#4A5553]">{card.back}</p></div>)}</div>}
  </div>;
}
function Register() {
  const nav = useNavigate(); const { setUser } = useAuth();
  const { t, dir } = useT();
  const [f, setF] = useState({ name:"", email:"", password:"", account_type:"parent" });
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (ev) => {
    ev.preventDefault(); setLoading(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/auth/register`, f);
      saveToken(data.token); setUser(data); nav("/");
    } catch (er) {
      let msg = "";
      if (!er.response) {
        msg = t("login.err_network");
      } else if (er.response.status === 400 && er.response.data?.detail) {
        msg = fmtErr(er.response.data.detail);
      } else if (er.response.status === 422) {
        msg = t("login.err_invalid");
      } else if (er.response.status >= 500) {
        msg = t("login.err_server");
      } else {
        msg = fmtErr(er.response.data?.detail) || `Error (${er.response.status})`;
      }
      setErr(msg);
      console.error("Register error:", er);
    } finally {
      setLoading(false);
    }
  };
  const acctTypes = [
    { key: "parent",         label: t("register.role_parent"),     desc: t("register.role_parent_desc"),     icon: User },
    { key: "inclusion_head", label: t("register.role_head"),       desc: t("register.role_head_desc"),       icon: Users },
    { key: "specialist",     label: t("register.role_specialist"), desc: t("register.role_specialist_desc"), icon: Stethoscope },
  ];
  return (
    <div className="min-h-screen flex items-center justify-center p-6" dir={dir} style={{background:"linear-gradient(135deg,#F9F8F6,#F0EFEB)"}}>
      <Card className="w-full max-w-2xl fade-up">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3"><Brain className="text-[#2F5233]" size={32}/><h1 className="text-3xl font-bold text-[#2F5233] font-heading">Inclusion+AI</h1></div>
          <LanguageToggle testid="register-lang-toggle"/>
        </div>
        <h2 className="text-xl font-semibold mb-2">{t("register.title")}</h2>
        <p className="text-sm text-[#4A5553] mb-5">{t("brand.tagline")}</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-[#1A201E] block mb-2">{t("register.account_type")}</label>
            <div className="grid sm:grid-cols-3 gap-2" data-testid="acct-types">
              {acctTypes.map(at => {
                const Icon = at.icon;
                const active = f.account_type === at.key;
                return <button type="button" key={at.key} onClick={()=>setF({...f, account_type:at.key})} data-testid={`acct-${at.key}`}
                  className={`p-3 rounded-lg border-2 text-start transition ${active?"border-[#2F5233] bg-[#2F5233]/5":"border-[#E5E2DB] hover:border-[#769FCD]"}`}>
                  <Icon size={20} className={active?"text-[#2F5233]":"text-[#4A5553]"}/>
                  <p className={`font-semibold text-sm mt-1 ${active?"text-[#2F5233]":"text-[#1A201E]"}`}>{at.label}</p>
                  <p className="text-[10px] text-[#4A5553] mt-0.5">{at.desc}</p>
                </button>;
              })}
            </div>
          </div>
          <Input data-testid="reg-name" placeholder={t("common.name")} value={f.name} onChange={e=>setF({...f,name:e.target.value})} required/>
          <Input data-testid="reg-email" type="email" placeholder={t("common.email")} value={f.email} onChange={e=>setF({...f,email:e.target.value})} required/>
          <Input data-testid="reg-password" type="password" placeholder={t("common.password") + " (8+)"} minLength={8} value={f.password} onChange={e=>setF({...f,password:e.target.value})} required/>
          {err && <p data-testid="reg-error" className="text-red-600 text-sm">{err}</p>}
          <Btn data-testid="reg-submit" type="submit" disabled={loading} className="w-full">{loading?"...":t("register.submit")}</Btn>
        </form>
        <p className="text-center text-sm mt-4 text-[#4A5553]">{t("register.have_account")} <Link to="/login" className="text-[#2F5233] font-medium hover:underline">{t("register.signin")}</Link></p>
      </Card>
    </div>
  );
}

// ============= LAYOUT =============
function Shell({ children }) {
  const { user, setUser } = useAuth(); const nav = useNavigate();
  const { t, lang, dir } = useT();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const notifFeed = useNotificationsFeed();
  const isMobile = useIsMobile();
  const logout = async () => { await axios.post(`${API}/auth/logout`); clearToken(); setUser(false); nav("/login"); };
  const closeDrawer = () => setDrawerOpen(false);
  const acct = user?.account_type || "parent";
  const acctLabel = { parent: t("nav.role_parent"), inclusion_head: t("nav.role_head"), specialist: t("nav.role_specialist"), super_admin: lang === "ar" ? "مدير المنصة" : "Super Admin" }[acct] || t("nav.role_parent");
  // Navigation per account type — strict role separation (v3.2)
  const isParent = acct === "parent";
  const isSpecialist = acct === "specialist";
  const isHead = acct === "inclusion_head";
  const isSuperAdmin = acct === "super_admin";
  const navItems = [
    // PARENT-FACING
    { to:"/", I: TrendingUp, label:t("nav.dashboard"), tid:"nav-dash", show: true },
    { to:"/children", I: User, label: isParent ? t("nav.children") : (lang === "ar" ? "الأطفال" : "Children"), tid:"nav-children", show: true },
    { to:"/advisor", I: MessageCircle, label:t("nav.advisor"), tid:"nav-advisor", show: !isSuperAdmin },
    { to:"/home-activities", I: BookOpen, label:t("nav.home_activities"), tid:"nav-home-activities", show: isParent },
    { to:"/certificates", I: Award, label:t("nav.certificates"), tid:"nav-certificates", show: isParent },
    { to:"/reminders", I: Bell, label:t("nav.reminders"), tid:"nav-reminders", show: !isSuperAdmin },
    // SPECIALIST + INCLUSION HEAD ONLY
    { to:"/cases", I: Users, label:t("nav.cases"), tid:"nav-cases", show: isSpecialist || isHead || isSuperAdmin },
    { to:"/curriculum", I: BookOpen, label:t("nav.curriculum"), tid:"nav-curriculum", show: isSpecialist || isHead || isSuperAdmin },
    { to:"/screening", I: Eye, label:t("nav.screening"), tid:"nav-screen", show: isSpecialist || isHead },
    { to:"/games", I: Award, label:t("nav.games"), tid:"nav-games", show: isSpecialist || isHead },
    // INCLUSION HEAD + SUPER ADMIN
    { to:"/team", I: Users, label: lang === "ar" ? "الفريق والحسابات" : "Team & Accounts", tid:"nav-team", show: isHead || isSuperAdmin },
    { to:"/analytics", I: BarChart2, label:t("nav.analytics"), tid:"nav-analytics", show: isHead || isSuperAdmin },
    { to:"/admin", I: Shield, label:t("nav.admin"), tid:"nav-admin", show: isSuperAdmin },
  ];
  const visibleNav = navItems.filter(it => it.show);
  const NavLink = ({ item, onClick }) => (
    <Link to={item.to} data-testid={item.tid} onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[#F0EFEB] active:bg-[#E5E2DB] text-[#1A201E] transition min-h-[44px]">
      <item.I size={18} className="flex-shrink-0 text-[#2F5233]"/>
      <span className="text-sm font-medium">{item.label}</span>
    </Link>
  );
  return (
    <div className="min-h-screen md:flex" dir={dir}>
      {/* ===== MOBILE TOP APP BAR (mobile only) ===== */}
      <header className="md:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E5E2DB]" data-testid="mobile-topbar" style={{paddingTop:"env(safe-area-inset-top)"}}>
        <div className="flex items-center justify-between px-3 h-14">
          <button data-testid="mobile-menu-btn" aria-label={lang === "ar" ? "فتح القائمة" : "Open menu"} onClick={()=>setDrawerOpen(true)}
            className="w-11 h-11 flex items-center justify-center rounded-lg hover:bg-[#F0EFEB] active:bg-[#E5E2DB] transition">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2F5233" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
          <Link to="/" className="flex items-center gap-2" data-testid="mobile-brand">
            <Brain className="text-[#2F5233]" size={22}/>
            <span className="text-lg font-bold text-[#2F5233] font-heading">Inclusion+AI</span>
          </Link>
          <div className="flex items-center gap-1">
            {isMobile && <NotificationsBell variant="mobile" feed={notifFeed}/>}
            <LanguageToggle testid="mobile-lang-toggle" compact/>
          </div>
        </div>
      </header>

      {/* ===== MOBILE DRAWER + BACKDROP ===== */}
      {drawerOpen && <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in" onClick={closeDrawer} data-testid="drawer-backdrop"/>
        <aside dir={dir}
          className={`absolute top-0 bottom-0 ${dir === "rtl" ? "right-0" : "left-0"} w-[86%] max-w-[320px] bg-white shadow-2xl flex flex-col overflow-y-auto`}
          style={{paddingTop:"env(safe-area-inset-top)", paddingBottom:"env(safe-area-inset-bottom)"}}>
          <div className="flex items-center justify-between px-4 h-14 border-b border-[#E5E2DB]">
            <div className="flex items-center gap-2">
              <Brain className="text-[#2F5233]" size={22}/>
              <span className="text-lg font-bold text-[#2F5233] font-heading">Inclusion+AI</span>
            </div>
            <button onClick={closeDrawer} data-testid="drawer-close" aria-label={lang === "ar" ? "إغلاق" : "Close"}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-[#F0EFEB]"><X size={20} className="text-[#4A5553]"/></button>
          </div>
          <div className="px-4 py-3 bg-[#F9F8F6] border-b border-[#E5E2DB]">
            <p className="text-[10px] text-[#4A5553] inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#2F5233]"/>{t("nav.mode")} <strong className="text-[#2F5233]">{acctLabel}</strong></p>
            <p className="text-sm font-medium mt-2 text-[#1A201E] truncate">{user?.name}</p>
            <p className="text-[11px] text-[#4A5553] truncate">{user?.email}</p>
          </div>
          <nav className="flex-1 p-3 space-y-1">
            {visibleNav.map(it => <NavLink key={it.to} item={it} onClick={closeDrawer}/>)}
          </nav>
          <div className="border-t border-[#E5E2DB] p-3">
            <button data-testid="mobile-logout-btn" onClick={()=>{ closeDrawer(); logout(); }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-red-50 active:bg-red-100 text-[#D9534F] w-full min-h-[44px]"><LogOut size={18}/><span className="text-sm font-medium">{t("common.logout")}</span></button>
          </div>
        </aside>
      </div>}

      {/* ===== DESKTOP SIDEBAR (md+) ===== */}
      <aside className={`w-64 bg-white ${dir === "rtl" ? "border-l" : "border-r"} border-[#E5E2DB] p-4 hidden md:flex flex-col`}>
        <div className="flex items-center gap-2 mb-2 p-2"><Brain className="text-[#2F5233]" size={26}/><span className="text-2xl font-bold text-[#2F5233] font-heading">Inclusion+AI</span></div>
        <p className="text-[10px] text-[#4A5553] px-2 mb-3 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#2F5233]"/>{t("nav.mode")} {acctLabel}</p>
        <div className="px-2 mb-3"><LanguageToggle testid="sidebar-lang-toggle"/></div>
        <nav className="flex-1 space-y-1">
          {visibleNav.map(it => <NavLink key={it.to} item={it}/>)}
        </nav>
        <div className="border-t border-[#E5E2DB] pt-4 mt-4">
          <div className="px-3 py-2 text-sm"><p className="font-medium">{user?.name}</p><p className="text-[#4A5553] text-xs">{user?.email}</p></div>
          <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-[#F0EFEB] text-[#4A5553] w-full"><LogOut size={16}/>{t("common.logout")}</button>
        </div>
      </aside>

      <main id="main-content" role="main" className="flex-1 p-4 sm:p-6 md:p-10 overflow-x-hidden pb-24 md:pb-10 relative">
        {!isMobile && <NotificationsBell variant="desktop" feed={notifFeed}/>}
        {children}
      </main>
    </div>
  );
}
const Protected = ({ children }) => { const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#4A5553]">...</div>;
  return user ? <Shell>{children}</Shell> : <Navigate to="/login"/>;
};

// Wrapper that provides `user` to the Team page from AuthContext
const TeamPageWrapper = () => {
  const { user } = useAuth();
  if (!user || !["inclusion_head", "super_admin"].includes(user.account_type)) return <Navigate to="/" replace/>;
  return <TeamManagementPage user={user}/>;
};

// ============= NOTIFICATIONS BELL (v3.5) =============
// Simple media-query hook — determines whether we're on the mobile breakpoint (<768px)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767.98px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767.98px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

// Shared polling hook — used by both mobile and desktop bell instances
function useNotificationsFeed() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const load = () => axios.get(`${API}/notifications/me?limit=15`).then(r => {
    setItems(r.data.items || []); setUnread(r.data.unread_count || 0);
  }).catch(() => {});
  useEffect(() => {
    load();
    const intv = setInterval(load, 30000);
    return () => clearInterval(intv);
  }, []);
  const markRead = async (n) => { if (n.read) return; await axios.post(`${API}/notifications/${n.id}/read`); load(); };
  const markAll = async () => { await axios.post(`${API}/notifications/read-all`); load(); };
  return { items, unread, markRead, markAll };
}

function NotificationsBell({ variant = "desktop", feed }) {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const { items, unread, markRead, markAll } = feed;
  const isMobile = variant === "mobile";
  const wrapperCls = isMobile
    ? "relative"
    : "absolute top-4 start-4 z-30";
  const dropdownAlign = isMobile
    ? "absolute mt-2 end-0 w-[min(20rem,calc(100vw-1.5rem))]"
    : "absolute mt-2 start-0 w-80 max-w-[calc(100vw-1.5rem)]";
  return <div className={wrapperCls} data-testid="notifications-bell">
    <button onClick={()=>setOpen(o=>!o)} data-testid="bell-toggle" className="relative w-11 h-11 md:w-10 md:h-10 rounded-full bg-white border border-[#E5E2DB] shadow-sm hover:bg-[#F9F8F6] active:bg-[#F0EFEB] flex items-center justify-center" aria-label={t("notif.title")}>
      <Bell size={18} className="text-[#2F5233]"/>
      {unread > 0 && <span data-testid="unread-badge" className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
    </button>
    {open && <div className={`${dropdownAlign} bg-white rounded-xl border border-[#E5E2DB] shadow-lg overflow-hidden z-50`} data-testid="bell-dropdown">
      <div className="flex justify-between items-center p-3 border-b border-[#E5E2DB] bg-[#F9F8F6]">
        <strong className="text-sm">{t("notif.title")}</strong>
        {unread > 0 && <button onClick={markAll} className="text-[10px] text-[#2F5233] underline" data-testid="mark-all-read">{t("notif.mark_all_read")}</button>}
      </div>
      <div className="max-h-96 overflow-y-auto">
        {items.length === 0 ? <p className="text-center py-8 text-xs text-[#4A5553]">{t("notif.empty")}</p> :
          items.map(n => <button key={n.id} onClick={()=>markRead(n)} data-testid={`notif-${n.id}`}
            className={`w-full text-start p-3 border-b border-[#F0EFEB] hover:bg-[#F9F8F6] transition ${n.read?"opacity-60":""}`}>
            <div className="flex items-start gap-2">
              {!n.read && <span className="w-2 h-2 mt-1.5 rounded-full bg-[#2F5233] flex-shrink-0"/>}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-xs">{n.subject}</p>
                <p className="text-[10px] text-[#4A5553] line-clamp-2 mt-0.5">{n.body}</p>
                <p className="text-[9px] text-[#4A5553] mt-1">{new Date(n.created_at).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}</p>
              </div>
            </div>
          </button>)}
      </div>
    </div>}
  </div>;
}

// ============= DASHBOARD =============
// ============= INCLUSION HEAD COMMAND DASHBOARD (v3.1) =============
const PIE_PALETTE = ["#2F5233", "#769FCD", "#E8A317", "#B8704A", "#5E8A6E", "#A67BA1", "#D17A47", "#4B7A8F", "#8AA67A", "#C49B6E", "#6E8294"];

function KPICard({ icon: Icon, label, value, color, sub, testid }) {
  return <div className="rounded-xl border border-[#E5E2DB] bg-white p-4 hover:shadow-md transition flex flex-col gap-1.5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs text-[#4A5553]">{label}</span>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}><Icon size={18}/></div>
    </div>
    <div className="text-3xl font-bold font-heading">{value}</div>
    {sub && <div className="text-[11px] text-[#4A5553]">{sub}</div>}
  </div>;
}

function timeAgo(iso, lang = "ar") {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - t) / 1000);
    if (lang === "en") {
      if (diff < 60) return "now";
      if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
      if (diff < 86400*7) return `${Math.floor(diff/86400)}d ago`;
      return new Date(iso).toLocaleDateString("en-US");
    }
    if (diff < 60) return "الآن";
    if (diff < 3600) return `قبل ${Math.floor(diff/60)} د`;
    if (diff < 86400) return `قبل ${Math.floor(diff/3600)} س`;
    if (diff < 86400*7) return `قبل ${Math.floor(diff/86400)} يوم`;
    return new Date(iso).toLocaleDateString("ar-EG");
  } catch { return ""; }
}

const ACTIVITY_LABELS = {
  case_created:       { ar: "أُنشئت حالة",        en: "Case created" },
  case_updated:       { ar: "حُدّثت حالة",        en: "Case updated" },
  goal_added:         { ar: "أُضيف هدف",          en: "Goal added" },
  goal_updated:       { ar: "حُدّث هدف",          en: "Goal updated" },
  goal_status:        { ar: "تغيّرت حالة هدف",   en: "Goal status changed" },
  goal_removed:       { ar: "حُذف هدف",           en: "Goal removed" },
  goal_progress:      { ar: "سُجّل تقدم لهدف",   en: "Goal progress logged" },
  note_added:         { ar: "أُضيفت ملاحظة",      en: "Note added" },
  member_added:       { ar: "أُضيف عضو",          en: "Member added" },
  member_removed:     { ar: "حُذف عضو",           en: "Member removed" },
  invite_sent:        { ar: "أُرسلت دعوة",        en: "Invite sent" },
  child_linked:       { ar: "تم ربط طفل",          en: "Child linked" },
  child_unlinked:     { ar: "فُكّ ربط طفل",       en: "Child unlinked" },
  evaluation_created: { ar: "أُنشئ تقييم فيديو",   en: "Video evaluation created" },
  status_changed:     { ar: "تغيّرت حالة الملف",  en: "File status changed" },
};

function InclusionHeadCommandDashboard({ user }) {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [trendsWindow, setTrendsWindow] = useState(30);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const load = () => {
    setLoading(true);
    axios.get(`${API}/dashboard/inclusion-head`)
      .then(r => { setData(r.data); setErr(""); })
      .catch(e => setErr(fmtErr(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  const loadTrends = (w) => {
    axios.get(`${API}/dashboard/inclusion-head/trends?window=${w}`)
      .then(r => setTrends(r.data))
      .catch(() => {});
  };
  useEffect(() => { load(); loadTrends(trendsWindow); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadTrends(trendsWindow); }, [trendsWindow]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !data) return <div className="p-8 text-center text-[#4A5553]" data-testid="dashboard-loading">{t("status.loading")}</div>;
  if (err && !data) return <div className="p-8 text-center"><p className="text-red-600 mb-3">{err}</p><Btn onClick={load}>{t("dash.retry")}</Btn></div>;
  if (!data) return null;
  const k = data.kpis;

  return <div className="space-y-7 fade-up" data-testid="inclusion-head-command-dashboard">
    {/* HEADER */}
    <div className="flex flex-wrap gap-4 justify-between items-start">
      <div>
        <div className="flex items-center gap-2 text-xs text-[#769FCD] font-semibold uppercase tracking-wider mb-1">
          <Shield size={14}/>{lang === "ar" ? "وضع رئيس قسم الدمج · لوحة القيادة" : "Inclusion Head mode · Command center"}
        </div>
        <h1 className="text-4xl font-bold font-heading">{t("dash.welcome")} {user?.name}</h1>
        <p className="text-[#4A5553] mt-1 text-sm">{lang === "ar" ? "نظرة بانورامية على القسم بأكمله — الحالات · الأهداف · الفرق · المتابعات" : "Panoramic view of the entire department — cases · goals · teams · follow-ups"}</p>
        <p className="text-[11px] text-[#4A5553] mt-1">{lang === "ar" ? "آخر تحديث:" : "Last updated:"} {timeAgo(data.generated_at, lang)} <button onClick={load} className="underline text-[#2F5233] me-2" data-testid="refresh-dashboard">{lang === "ar" ? "تحديث" : "Refresh"}</button></p>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Link to="/children"><Btn variant="outline" data-testid="quick-children"><Users size={14} className="inline ms-1"/>{lang === "ar" ? "سجل الأطفال" : "Children registry"}</Btn></Link>
        <Link to="/cases"><Btn data-testid="quick-cases"><Briefcase size={14} className="inline ms-1"/>{lang === "ar" ? "إدارة الحالات" : "Manage cases"}</Btn></Link>
      </div>
    </div>

    {/* KPIs ROW 1 — Department scale */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KPICard testid="kpi-children" icon={Users} label={lang === "ar" ? "إجمالي الأطفال" : "Total children"} value={k.children_total} color="bg-[#2F5233]/10 text-[#2F5233]" sub={`${k.cases_linked_to_children} ${lang === "ar" ? "حالة مربوطة" : "linked cases"}`}/>
      <KPICard testid="kpi-cases" icon={Briefcase} label={lang === "ar" ? "إجمالي الحالات" : "Total cases"} value={k.cases_total} color="bg-[#769FCD]/15 text-[#527598]" sub={`${k.cases_active} ${lang === "ar" ? "نشطة" : "active"} · ${k.cases_paused} ${lang === "ar" ? "معلّقة" : "paused"} · ${k.cases_closed} ${lang === "ar" ? "مغلقة" : "closed"}`}/>
      <KPICard testid="kpi-team-cases" icon={Stethoscope} label={lang === "ar" ? "حالات بفريق" : "Cases with team"} value={k.cases_with_team} color="bg-[#E8A317]/15 text-[#A36F00]" sub={`${k.cases_total ? Math.round(k.cases_with_team/k.cases_total*100) : 0}% ${lang === "ar" ? "من إجمالي الحالات" : "of all cases"}`}/>
      <KPICard testid="kpi-plan-cases" icon={Target} label={lang === "ar" ? "خطط مفعّلة" : "Active plans"} value={k.cases_with_plan} color="bg-emerald-100 text-emerald-700" sub={`${k.cases_total ? Math.round(k.cases_with_plan/k.cases_total*100) : 0}% ${lang === "ar" ? "من الحالات" : "of cases"}`}/>
    </div>

    {/* TIME-BASED TRENDS (v3.5) — 30/90 day analytics */}
    {trends && <Card data-testid="trends-section" className="border-2 border-[#769FCD]/30">
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold font-heading flex items-center gap-2"><TrendingUp size={18} className="text-[#769FCD]"/>{lang === "ar" ? "المؤشرات الزمنية للقسم" : "Department time-based metrics"}</h3>
          <p className="text-[10px] text-[#4A5553]">{lang === "ar" ? `تتبّع نمو الحالات والأهداف والإنجازات عبر آخر ${trendsWindow} يوم` : `Track growth of cases, goals and achievements over the last ${trendsWindow} days`}</p>
        </div>
        <div className="inline-flex rounded-lg border border-[#E5E2DB] overflow-hidden" data-testid="trends-window-switcher">
          {[7, 30, 90, 180].map(w => <button key={w} onClick={()=>setTrendsWindow(w)} data-testid={`trends-${w}`}
            className={`px-3 py-1.5 text-xs transition ${trendsWindow===w?"bg-[#2F5233] text-white":"bg-white text-[#4A5553] hover:bg-[#F0EFEB]"}`}>
            {w} {lang === "ar" ? "يوم" : "days"}
          </button>)}
        </div>
      </div>
      {/* Compact totals row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4 text-center">
        <div className="p-2 rounded-lg bg-[#F9F8F6]" data-testid="trend-total-new-cases"><p className="text-[10px] text-[#4A5553]">{lang === "ar" ? "حالات جديدة" : "New cases"}</p><p className="text-xl font-bold text-[#2F5233]">{trends.totals.new_cases}</p></div>
        <div className="p-2 rounded-lg bg-[#F9F8F6]" data-testid="trend-total-new-children"><p className="text-[10px] text-[#4A5553]">{lang === "ar" ? "أطفال جدد" : "New children"}</p><p className="text-xl font-bold text-[#527598]">{trends.totals.new_children}</p></div>
        <div className="p-2 rounded-lg bg-[#F9F8F6]" data-testid="trend-total-goals-created"><p className="text-[10px] text-[#4A5553]">{lang === "ar" ? "أهداف مُنشأة" : "Goals created"}</p><p className="text-xl font-bold text-[#A36F00]">{trends.totals.goals_created}</p></div>
        <div className="p-2 rounded-lg bg-[#F9F8F6]" data-testid="trend-total-goals-achieved"><p className="text-[10px] text-[#4A5553]">{lang === "ar" ? "أهداف مُنجَزة" : "Goals achieved"}</p><p className="text-xl font-bold text-emerald-700">{trends.totals.goals_achieved}</p></div>
        <div className="p-2 rounded-lg bg-[#F9F8F6]" data-testid="trend-total-progress"><p className="text-[10px] text-[#4A5553]">{lang === "ar" ? "جلسات تقدم" : "Progress sessions"}</p><p className="text-xl font-bold text-[#769FCD]">{trends.totals.progress_sessions}</p></div>
        <div className="p-2 rounded-lg bg-[#F9F8F6]" data-testid="trend-total-evaluations"><p className="text-[10px] text-[#4A5553]">{lang === "ar" ? "تقييمات فيديو" : "Video evaluations"}</p><p className="text-xl font-bold text-rose-600">{trends.totals.evaluations}</p></div>
      </div>
      {/* Activity Line Chart */}
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={trends.labels.map((d, i) => ({
          date: d.slice(5),
          new_cases: trends.series.new_cases[i],
          goals_achieved: trends.series.goals_achieved[i],
          progress: trends.series.progress_sessions[i],
          evals: trends.series.evaluations[i],
        }))} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DB"/>
          <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={trendsWindow > 60 ? 14 : (trendsWindow > 14 ? 4 : 1)}/>
          <YAxis tick={{ fontSize: 10 }} allowDecimals={false}/>
          <Tooltip/>
          <Legend wrapperStyle={{ fontSize: 11 }}/>
          <Line type="monotone" dataKey="new_cases" name={lang === "ar" ? "حالات جديدة" : "New cases"} stroke="#2F5233" strokeWidth={2} dot={false}/>
          <Line type="monotone" dataKey="goals_achieved" name={lang === "ar" ? "أهداف مُنجَزة" : "Goals achieved"} stroke="#10B981" strokeWidth={2} dot={false}/>
          <Line type="monotone" dataKey="progress" name={lang === "ar" ? "جلسات تقدم" : "Progress sessions"} stroke="#769FCD" strokeWidth={2} dot={false}/>
          <Line type="monotone" dataKey="evals" name={lang === "ar" ? "تقييمات" : "Evaluations"} stroke="#E8A317" strokeWidth={2} dot={false}/>
        </LineChart>
      </ResponsiveContainer>
      {/* Rolling achievement rate */}
      <div className="mt-4 pt-3 border-t border-[#E5E2DB]">
        <h4 className="text-xs font-semibold text-[#4A5553] mb-2">{lang === "ar" ? "معدّل تحقيق الأهداف (متوسط 7 أيام متدحرج)" : "Goal achievement rate (7-day rolling average)"}</h4>
        <ResponsiveContainer width="100%" height={140}>
          <LineChart data={trends.labels.map((d, i) => ({ date: d.slice(5), rate: trends.rolling_achievement_rate[i] }))} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DB"/>
            <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={trendsWindow > 60 ? 14 : (trendsWindow > 14 ? 4 : 1)}/>
            <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} unit="%"/>
            <Tooltip formatter={(v) => `${v}%`}/>
            <Line type="monotone" dataKey="rate" stroke="#10B981" strokeWidth={2.5} dot={false} fill="#10B98120"/>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>}

    {/* KPIs ROW 2 — Goals & Outcomes */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KPICard testid="kpi-goals-total" icon={Target} label={lang === "ar" ? "إجمالي الأهداف" : "Total goals"} value={k.goals_total} color="bg-[#769FCD]/15 text-[#527598]" sub={`${k.goals_active} ${lang === "ar" ? "نشطة" : "active"}`}/>
      <KPICard testid="kpi-achievement" icon={TrendingUp} label={lang === "ar" ? "معدّل تحقيق الأهداف" : "Goal achievement rate"} value={`${k.goal_achievement_rate}%`} color="bg-emerald-100 text-emerald-700" sub={`${k.goals_achieved} ${lang === "ar" ? "هدف مُنجَز" : "achieved"}`}/>
      <KPICard testid="kpi-evals" icon={Activity} label={lang === "ar" ? "متوسط نقاط التقييمات" : "Avg evaluation score"} value={`${k.avg_evaluation_score}%`} color="bg-[#E8A317]/15 text-[#A36F00]" sub={`${k.evaluations_total} ${lang === "ar" ? "تقييم Pre/Post" : "Pre/Post evals"}`}/>
      <KPICard testid="kpi-invites" icon={Zap} label={lang === "ar" ? "دعوات معلّقة" : "Pending invites"} value={k.pending_invites} color="bg-red-100 text-red-700" sub={k.pending_invites>0?(lang === "ar" ? "بانتظار قبول المتخصصين" : "Awaiting specialists' acceptance"):(lang === "ar" ? "كل الدعوات مُنجَزة" : "All invites processed")}/>
    </div>

    {/* CHARTS */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Card data-testid="chart-support-distribution">
        <h3 className="text-lg font-semibold font-heading mb-3 flex items-center gap-2"><BarChart3 size={18} className="text-[#2F5233]"/>{lang === "ar" ? "توزيع الحالات حسب فئات الدعم" : "Cases distribution by support category"}</h3>
        {data.support_distribution.length === 0 ? <p className="text-sm text-[#4A5553] text-center py-12">{lang === "ar" ? "لا توجد بيانات بعد" : "No data yet"}</p> : <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.support_distribution.map(d => ({ ...d, label: (lang === "en" && d.label_en) || d.label_ar }))} layout="vertical" margin={{ top: 5, right: 25, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DB"/>
            <XAxis type="number" tick={{ fontSize: 11 }}/>
            <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={140}/>
            <Tooltip cursor={{ fill: "#F9F8F6" }}/>
            <Bar dataKey="value" fill="#2F5233" radius={[0, 6, 6, 0]}/>
          </BarChart>
        </ResponsiveContainer>}
      </Card>
      <Card data-testid="chart-domain-distribution">
        <h3 className="text-lg font-semibold font-heading mb-3 flex items-center gap-2"><Target size={18} className="text-[#2F5233]"/>{lang === "ar" ? "الأهداف حسب المجال التطوّري" : "Goals by developmental domain"}</h3>
        {data.domain_distribution.every(d => d.value === 0) ? <p className="text-sm text-[#4A5553] text-center py-12">{lang === "ar" ? "لم تُسجَّل أهداف بعد" : "No goals logged yet"}</p> : <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={data.domain_distribution.map(d => ({ ...d, label: (lang === "en" && d.label_en) || d.label_ar }))} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={50} outerRadius={95} paddingAngle={3} label={(e)=>e.value>0?e.value:""}>
              {data.domain_distribution.map((_, i) => <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]}/>)}
            </Pie>
            <Tooltip/>
            <Legend wrapperStyle={{ fontSize: 11 }}/>
          </PieChart>
        </ResponsiveContainer>}
      </Card>
    </div>

    {/* INCLUSION SETTING + URGENT */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <Card data-testid="inclusion-settings">
        <h3 className="text-base font-semibold mb-3 flex items-center gap-2"><BookOpen size={16} className="text-[#2F5233]"/>{lang === "ar" ? "إطار الدمج" : "Inclusion setting"}</h3>
        {data.inclusion_setting_distribution.length === 0 ? <p className="text-sm text-[#4A5553] text-center py-8">{lang === "ar" ? "لا توجد بيانات" : "No data"}</p> :
          <div className="space-y-2.5">{data.inclusion_setting_distribution.map((s) => {
            const max = Math.max(...data.inclusion_setting_distribution.map(x => x.value)) || 1;
            const pct = Math.round(s.value/max*100);
            return <div key={s.key}>
              <div className="flex justify-between text-xs mb-1"><span>{(lang === "en" && s.label_en) || s.label_ar}</span><strong>{s.value}</strong></div>
              <div className="h-2 bg-[#F0EFEB] rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#2F5233] to-[#769FCD]" style={{width:`${pct}%`}}/></div>
            </div>;
          })}</div>}
      </Card>

      <Card className="lg:col-span-2 border-r-4 border-r-red-400" data-testid="urgent-cases">
        <h3 className="text-base font-semibold mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-red-600"/>{lang === "ar" ? "حالات تحتاج متابعة عاجلة" : "Cases needing urgent follow-up"} <span className="text-xs font-normal text-[#4A5553]">({data.urgent_cases.length})</span></h3>
        {data.urgent_cases.length === 0 ? <div className="text-center py-6 text-sm text-emerald-700 bg-emerald-50 rounded-lg"><CheckCircle2 className="inline ms-1" size={16}/>{lang === "ar" ? "لا توجد حالات تحتاج متابعة عاجلة" : "No cases need urgent follow-up"}</div> :
          <div className="space-y-2 max-h-[320px] overflow-y-auto pe-1">{data.urgent_cases.map(u => <Link key={u.id} to={`/cases/${u.id}`} data-testid={`urgent-${u.id}`}>
            <div className="p-3 rounded-lg border border-red-100 hover:border-red-300 bg-red-50/40 hover:bg-red-50 transition flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{u.name}</p>
                <p className="text-[11px] text-[#4A5553]">{u.age} {lang === "ar" ? "سنة" : "yrs"} · {u.team_size} {lang === "ar" ? "متخصص" : "specialist(s)"}</p>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {u.reasons.map((r, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{typeof r === "string" ? r : ((lang === "en" ? r.en : r.ar) || r.code)}</span>)}
                </div>
              </div>
              <ChevronLeft size={16} className="text-red-400 flex-shrink-0 mt-1"/>
            </div>
          </Link>)}</div>}
      </Card>
    </div>

    {/* ACTIVITY + TEAM PERFORMANCE */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Card data-testid="recent-activity-feed">
        <h3 className="text-base font-semibold mb-3 flex items-center gap-2"><Activity size={16} className="text-[#2F5233]"/>{lang === "ar" ? "آخر الأنشطة عبر القسم" : "Recent activity across the department"}</h3>
        {data.recent_activity.length === 0 ? <p className="text-sm text-[#4A5553] text-center py-8">{lang === "ar" ? "لا توجد أنشطة بعد" : "No activity yet"}</p> :
          <div className="space-y-1 max-h-[360px] overflow-y-auto pe-1">{data.recent_activity.map((a, i) => <Link key={`${a.case_id}-${i}-${a.ts||""}`} to={`/cases/${a.case_id}`} data-testid={`activity-${i}`}>
            <div className="flex items-start gap-2 p-2 rounded-lg hover:bg-[#F9F8F6] transition border-b border-[#F0EFEB] last:border-b-0">
              <div className="w-2 h-2 rounded-full bg-[#2F5233] mt-1.5 flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                <p className="text-xs"><strong className="text-[#2F5233]">{(ACTIVITY_LABELS[a.action] && (ACTIVITY_LABELS[a.action][lang] || ACTIVITY_LABELS[a.action].ar)) || a.action}</strong>
                  {a.detail && <span className="text-[#4A5553]"> — {a.detail}</span>}</p>
                <p className="text-[10px] text-[#4A5553] mt-0.5">{lang === "ar" ? "في حالة:" : "In case:"} <span className="font-medium">{a.case_name}</span> · {lang === "ar" ? "بواسطة" : "by"} {a.user_name || "—"} · {timeAgo(a.ts, lang)}</p>
              </div>
            </div>
          </Link>)}</div>}
      </Card>

      <Card data-testid="team-performance">
        <h3 className="text-base font-semibold mb-3 flex items-center gap-2"><Sparkles size={16} className="text-[#2F5233]"/>{lang === "ar" ? "أداء الفريق" : "Team performance"} <span className="text-xs font-normal text-[#4A5553]">({lang === "ar" ? "الأكثر نشاطاً" : "most active"})</span></h3>
        {data.team_performance.length === 0 ? <p className="text-sm text-[#4A5553] text-center py-8">{lang === "ar" ? "لم يُسجّل أعضاء الفريق ملاحظات بعد" : "Team members have not logged notes yet"}</p> :
          <div className="space-y-2 max-h-[360px] overflow-y-auto pe-1">{data.team_performance.map((m, i) => <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#F9F8F6] border border-[#E5E2DB]" data-testid={`team-row-${i}`}>
            <div className="w-9 h-9 rounded-full bg-[#2F5233] text-white flex items-center justify-center font-bold flex-shrink-0">{(m.specialist_name || "?").charAt(0)}</div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{m.specialist_name}</p>
              <p className="text-[10px] text-[#4A5553]">{m.role} · {lang === "ar" ? "آخر نشاط" : "last activity"} {timeAgo(m.last_activity, lang)}</p>
            </div>
            <div className="text-end flex-shrink-0">
              <p className="text-lg font-bold text-[#2F5233]">{m.notes_count}</p>
              <p className="text-[9px] text-[#4A5553]">{lang === "ar" ? "ملاحظة" : "note(s)"}</p>
            </div>
          </div>)}</div>}
      </Card>
    </div>

    {/* RECENT CASES */}
    <Card data-testid="recent-cases-list">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-semibold flex items-center gap-2"><Briefcase size={16} className="text-[#2F5233]"/>{lang === "ar" ? "آخر الحالات المُحدَّثة" : "Recently updated cases"}</h3>
        <Link to="/cases"><Btn variant="ghost"><ArrowRight size={14} className="inline ms-1"/>{lang === "ar" ? "إدارة الكل" : "Manage all"}</Btn></Link>
      </div>
      {data.recent_cases.length === 0 ? <p className="text-sm text-[#4A5553] text-center py-8">{lang === "ar" ? "لم تُنشأ حالات بعد" : "No cases created yet"}</p> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{data.recent_cases.map(c => <Link key={c.id} to={`/cases/${c.id}`} data-testid={`recent-case-${c.id}`}>
          <div className="p-3 rounded-lg border border-[#E5E2DB] hover:border-[#2F5233] bg-white transition">
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{c.name}</p>
                <p className="text-[11px] text-[#4A5553]">{c.age} {lang === "ar" ? "سنة" : "yrs"} · {c.team_size} {lang === "ar" ? "متخصص" : "specialist(s)"} {c.child_id && `· ${lang === "ar" ? "مرتبطة بطفل ●" : "linked to child ●"}`}</p>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.status==="active"?"bg-emerald-100 text-emerald-700":"bg-[#F0EFEB] text-[#4A5553]"}`}>{c.status === "active" ? (lang === "ar" ? "نشطة" : "Active") : c.status}</span>
            </div>
          </div>
        </Link>)}</div>}
    </Card>
  </div>;
}

function Dashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const acct = user?.account_type || "parent";
  const [children, setChildren] = useState([]);
  const [cases, setCases] = useState([]);
  useEffect(() => {
    if (acct === "parent") axios.get(`${API}/children`).then(r => setChildren(r.data));
    if (acct === "specialist") axios.get(`${API}/cases`).then(r => setCases(r.data));
  }, [acct]);

  // Inclusion Head: dedicated command dashboard (no specialist/teacher cards)
  // Super Admin also sees the head command dashboard (global scope, no filter)
  if (acct === "inclusion_head" || acct === "super_admin") {
    return <InclusionHeadCommandDashboard user={user}/>;
  }

  // Specialist: focused on assigned cases only
  if (acct === "specialist") {
    const active = cases.filter(c => c.status === "active").length;
    const withPlans = cases.filter(c => c.plan && Object.values(c.plan).some(arr => (arr||[]).length > 0)).length;
    return <div data-testid="dashboard-specialist" className="space-y-8 fade-up">
      <div><h1 className="text-4xl font-bold font-heading mb-2">{t("dash.welcome")} {user?.name}</h1>
        <p className="text-[#4A5553]">{t("dash.specialist_subtitle")}</p></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card><p className="text-sm text-[#4A5553]">{t("dash.spec_kpi_my_cases")}</p><p className="text-4xl font-bold text-[#2F5233] mt-2">{cases.length}</p></Card>
        <Card><p className="text-sm text-[#4A5553]">{t("dash.spec_kpi_active")}</p><p className="text-4xl font-bold text-[#769FCD] mt-2">{active}</p></Card>
        <Card><p className="text-sm text-[#4A5553]">{t("dash.spec_kpi_plans")}</p><p className="text-4xl font-bold text-[#E8A317] mt-2">{withPlans}</p></Card>
      </div>
      <Card>
        <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-semibold font-heading">{t("dash.spec_latest_cases")}</h2>
          <Link to="/cases"><Btn variant="outline" data-testid="dash-cases-all">{t("dash.spec_view_all")}</Btn></Link></div>
        {cases.length === 0 ? <p className="text-[#4A5553] text-center py-8">{t("dash.spec_empty")}</p> :
          <div className="space-y-2">{cases.slice(0,5).map(c => <div key={c.id} className="flex justify-between items-center p-3 rounded-lg hover:bg-[#F9F8F6]">
            <div><p className="font-medium">{c.name}</p><p className="text-xs text-[#4A5553]">{c.age} {t("dash.spec_years")} · {c.team?.length||0} {t("dash.spec_specialists")}</p></div>
            <Link to={`/cases/${c.id}`}><Btn variant="ghost">{t("dash.spec_open")}</Btn></Link>
          </div>)}</div>}
      </Card>
    </div>;
  }

  // Parent dashboard — fully redesigned (v3.2): goal progress + certificates + AI advisor + home activities
  return <ParentCommandDashboard user={user}/>;
}

// ============= PARENT COMMAND DASHBOARD (v3.2) =============
function ParentCommandDashboard({ user }) {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const load = () => {
    setLoading(true);
    axios.get(`${API}/dashboard/parent`)
      .then(r => { setData(r.data); setErr(""); })
      .catch(e => setErr(fmtErr(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return <div className="p-8 text-center text-[#4A5553]">{t("dash.loading_dashboard")}</div>;
  if (err && !data) return <div className="p-8 text-center"><p className="text-red-600 mb-3">{err}</p><Btn onClick={load}>{t("dash.retry")}</Btn></div>;
  if (!data) return null;
  const k = data.kpis;
  // Trend chart: progress entries grouped by date
  const trendData = (() => {
    const map = {};
    for (const p of data.progress_timeline) {
      if (!p.date) continue;
      const d = p.date.slice(0, 10);
      if (!map[d]) map[d] = { date: d, rate: 0, count: 0 };
      map[d].rate += p.rate || 0;
      map[d].count++;
    }
    return Object.values(map).map(r => ({ date: r.date.slice(5), rate: Math.round(r.rate / r.count) })).reverse().slice(-14);
  })();

  return <div className="space-y-6 fade-up" data-testid="parent-command-dashboard">
    {/* Hero */}
    <div className="rounded-2xl border-2 border-[#2F5233]/15 bg-gradient-to-l from-[#2F5233]/8 via-[#769FCD]/5 to-transparent p-5">
      <div className="flex items-center gap-2 text-xs text-[#769FCD] font-semibold tracking-wider uppercase mb-1"><User size={14}/>{t("dash.parent_mode")}</div>
      <h1 className="text-3xl md:text-4xl font-bold font-heading">{t("dash.welcome")} {user?.name} 💚</h1>
      <p className="text-sm text-[#4A5553] mt-1">{t("dash.parent_subtitle")}</p>
    </div>

    {/* KPIs */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KPICard testid="p-kpi-children" icon={Users} label={t("dash.kpi_children")} value={k.children_count} color="bg-[#2F5233]/10 text-[#2F5233]" sub={k.children_count>0?t("dash.kpi_children_sub_active"):t("dash.kpi_children_sub_empty")}/>
      <KPICard testid="p-kpi-goals" icon={Target} label={t("dash.kpi_goals")} value={k.goals_active} color="bg-[#769FCD]/15 text-[#527598]" sub={`${k.goals_total} ${t("dash.kpi_goals_total")}`}/>
      <KPICard testid="p-kpi-achieved" icon={Award} label={t("dash.kpi_achieved")} value={k.goals_achieved} color="bg-emerald-100 text-emerald-700" sub={`${k.achievement_rate}% ${t("dash.kpi_achievement_rate")}`}/>
      <KPICard testid="p-kpi-activities" icon={BookOpen} label={t("dash.kpi_activities")} value={k.home_activities_count} color="bg-[#E8A317]/15 text-[#A36F00]" sub={t("dash.kpi_activities_sub")}/>
    </div>

    {/* Children quick cards */}
    {data.children.length > 0 && <Card data-testid="parent-children-section">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-lg font-semibold font-heading flex items-center gap-2"><Users size={18} className="text-[#2F5233]"/>{t("children.title_parent")}</h2>
        <Link to="/children"><Btn variant="ghost"><Plus size={12} className="inline ms-1"/>{lang === "ar" ? "إدارة الأطفال" : "Manage children"}</Btn></Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.children.map(c => <Link key={c.id} to={`/children/${c.id}`} data-testid={`p-child-${c.id}`}>
          <div className="p-3 rounded-lg border border-[#E5E2DB] hover:border-[#2F5233] bg-white transition flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#2F5233] text-white flex items-center justify-center font-bold flex-shrink-0">{(c.name || "?").charAt(0)}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{c.name}</p>
              <p className="text-[11px] text-[#4A5553]">{c.age} {t("children.years")} · {displayGender(c.gender, lang)}</p>
              <div className="flex gap-2 mt-1.5">
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#769FCD]/15 text-[#527598]">{c.goals_active} {lang === "ar" ? "أهداف نشطة" : "active goals"}</span>
                {c.certificates_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{c.certificates_count} {lang === "ar" ? "شهادة" : "cert(s)"}</span>}
              </div>
              <div className="mt-1.5 h-1.5 bg-[#F0EFEB] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#2F5233] to-[#769FCD]" style={{width:`${c.achievement_rate}%`}}/>
              </div>
              <p className="text-[10px] text-[#4A5553] mt-0.5">{lang === "ar" ? "معدّل النجاح:" : "Success rate:"} {c.avg_success_rate}%</p>
            </div>
          </div>
        </Link>)}
      </div>
    </Card>}

    {/* Progress Trend */}
    {trendData.length > 0 && <Card data-testid="parent-trend-chart">
      <h2 className="text-lg font-semibold font-heading mb-3 flex items-center gap-2"><TrendingUp size={18} className="text-[#2F5233]"/>{lang === "ar" ? "تقدّم أبنائك (آخر الأيام)" : "Your children's progress (recent days)"}</h2>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={trendData} margin={{ top:5, right:5, left:5, bottom:5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DB"/>
          <XAxis dataKey="date" tick={{ fontSize: 11 }}/>
          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }}/>
          <Tooltip/>
          <Line type="monotone" dataKey="rate" stroke="#2F5233" strokeWidth={2.5} dot={{ r: 4, fill: "#2F5233" }}/>
        </LineChart>
      </ResponsiveContainer>
      <p className="text-[10px] text-[#4A5553] mt-1">{lang === "ar" ? "معدّل النجاح من 100 — يُجمَع من جلسات التدريب المسجّلة لأهداف أبنائك." : "Success rate out of 100 — aggregated from recorded training sessions on your children's goals."}</p>
    </Card>}

    {/* AI Advisor + Quick Actions */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card data-testid="parent-advisor-card" className="bg-gradient-to-tl from-[#769FCD]/10 to-transparent border-[#769FCD]/30">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#769FCD] text-white flex items-center justify-center flex-shrink-0"><MessageCircle size={22}/></div>
          <div className="flex-1">
            <h3 className="font-semibold text-base mb-1">{lang === "ar" ? "المستشار الذكي لولي الأمر" : "Parent AI Advisor"}</h3>
            <p className="text-xs text-[#4A5553] mb-3">{lang === "ar" ? "اسأل عن سلوك طفلك، التواصل، التغذية، النوم، الواجبات، أو أي قلق تربوي — يجيبك خبير ذكاء اصطناعي مدرَّب على التربية والدمج." : "Ask about your child's behavior, communication, nutrition, sleep, homework, or any parenting concern — an AI expert trained in education and inclusion answers you."}</p>
            <Link to="/advisor"><Btn variant="accent" data-testid="open-advisor"><MessageCircle size={14} className="inline ms-1"/>{lang === "ar" ? "ابدأ المحادثة" : "Start chat"}</Btn></Link>
          </div>
        </div>
      </Card>
      <Card data-testid="parent-activities-card" className="bg-gradient-to-tl from-[#E8A317]/8 to-transparent border-[#E8A317]/30">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#E8A317] text-white flex items-center justify-center flex-shrink-0"><BookOpen size={22}/></div>
          <div className="flex-1">
            <h3 className="font-semibold text-base mb-1">{t("ha.title")}</h3>
            <p className="text-xs text-[#4A5553] mb-3">{k.home_activities_count} {lang === "ar" ? "نشاط مولّد بالذكاء الاصطناعي مرتبط بأهداف أبنائك — جاهز للتطبيق في البيت." : "AI-generated activities linked to your children's goals — ready for home practice."}</p>
            <Link to="/home-activities"><Btn data-testid="open-activities">{lang === "ar" ? "عرض الأنشطة" : "View activities"}</Btn></Link>
          </div>
        </div>
      </Card>
    </div>

    {/* Certificates */}
    <Card data-testid="parent-certificates-card">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-lg font-semibold font-heading flex items-center gap-2"><Award size={18} className="text-emerald-600"/>{t("cert.title")} ({k.certificates_count})</h2>
        {k.certificates_count > 0 && <Link to="/certificates"><Btn variant="ghost"><ArrowRight size={14} className="inline ms-1"/>{t("dash.spec_view_all")}</Btn></Link>}
      </div>
      {data.certificates.length === 0 ? <div className="text-center py-8 text-sm text-[#4A5553] bg-[#F9F8F6] rounded-lg">
        <Award className="mx-auto mb-2 text-emerald-400" size={32}/>
        <p>{lang === "ar" ? "لا توجد شهادات بعد." : "No certificates yet."}</p>
        <p className="text-xs mt-1">{t("cert.empty")}</p>
      </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.certificates.map(cert => <div key={cert.id} className="p-3 rounded-lg border-2 border-emerald-200 bg-emerald-50/40" data-testid={`p-cert-${cert.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <Award size={20} className="text-emerald-600 flex-shrink-0"/>
            <p className="font-mono text-[10px] text-emerald-700">{cert.id}</p>
          </div>
          <p className="text-xs font-semibold text-emerald-800 mb-1">{cert.child_name}</p>
          <p className="text-xs line-clamp-2" title={cert.goal_title}>{cert.goal_title || (lang === "ar" ? "هدف منجَز" : "Achieved goal")}</p>
          {cert.mastery_pct !== undefined && <p className="text-[10px] text-emerald-700 mt-1">{lang === "ar" ? "نسبة الإتقان:" : "Mastery:"} <strong>{cert.mastery_pct}%</strong></p>}
          {cert.issued_at && <p className="text-[10px] text-[#4A5553] mt-1">{new Date(cert.issued_at).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US")}</p>}
        </div>)}
      </div>}
    </Card>
  </div>;
}

// ============= CHILDREN LIST =============
function Children() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const acct = user?.account_type || "parent";
  const [list, setList] = useState([]); const [show, setShow] = useState(false);
  const [f, setF] = useState({ name:"", age:"", gender:"male", notes:"" });
  const load = () => axios.get(`${API}/children`).then(r => setList(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async (e) => { e.preventDefault();
    // Persist localized gender label
    const genderLabel = f.gender === "female" ? (lang === "ar" ? "أنثى" : "Female") : (lang === "ar" ? "ذكر" : "Male");
    await axios.post(`${API}/children`, { ...f, gender: genderLabel, age: parseInt(f.age) });
    setShow(false); setF({ name:"", age:"", gender:"male", notes:"" }); load();
  };
  const acctIntro = {
    parent: lang === "ar" ? "أطفالك. أضف طفلاً جديداً وابدأ التقييم وخطة التدخل، ثم اربطها بحالة دمج رسمية." : "Your children. Add a new child to start assessment and intervention planning, then link to an official inclusion case.",
    inclusion_head: lang === "ar" ? "سجل الأطفال على مستوى القسم. كل طفل هو نقطة الانطلاق لربط حالات التدخل والأهداف والتقييمات." : "Department-wide children registry. Each child is the starting point for linking intervention cases, goals and evaluations.",
    specialist: lang === "ar" ? "الأطفال المرتبطون بحالاتك. تواصل مع المنسق لإضافة طفل جديد." : "Children linked to your cases. Contact the coordinator to add a new child.",
  }[acct];
  return (
    <div className="space-y-6 fade-up" data-testid="children-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold font-heading">{acct === "parent" ? t("children.title_parent") : t("children.title_other")}</h1>
          <p className="text-sm text-[#4A5553] mt-1 max-w-2xl">{acctIntro}</p>
        </div>
        <Btn data-testid="add-child-btn" onClick={() => setShow(true)}><Plus className="inline ms-2" size={16}/>{t("children.add").replace("+ ", "")}</Btn>
      </div>
      {show && <Card><form onSubmit={add} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="child-form">
        <Input placeholder={t("children.placeholder_name")} value={f.name} onChange={e=>setF({...f,name:e.target.value})} required data-testid="child-name"/>
        <Input type="number" placeholder={t("children.placeholder_age")} value={f.age} onChange={e=>setF({...f,age:e.target.value})} required data-testid="child-age" min={1} max={18}/>
        <select className="px-4 py-2.5 rounded-lg border border-[#E5E2DB] bg-white" value={f.gender} onChange={e=>setF({...f,gender:e.target.value})} data-testid="child-gender">
          <option value="male">{lang === "ar" ? "ذكر" : "Male"}</option>
          <option value="female">{lang === "ar" ? "أنثى" : "Female"}</option>
        </select>
        <Input placeholder={lang === "ar" ? "ملاحظات" : "Notes"} value={f.notes} onChange={e=>setF({...f,notes:e.target.value})} data-testid="child-notes"/>
        <div className="md:col-span-2 flex gap-3"><Btn type="submit" data-testid="child-save">{t("common.save")}</Btn><Btn variant="outline" type="button" onClick={()=>setShow(false)}>{t("common.cancel")}</Btn></div>
      </form></Card>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {list.map(c => <Link key={c.id} to={`/children/${c.id}`} data-testid={`child-card-${c.id}`}><Card className="hover:border-[#2F5233] cursor-pointer transition">
          <h3 className="text-lg font-semibold">{c.name}</h3>
          <p className="text-sm text-[#4A5553] mt-1">{c.age} {t("children.years")} · {displayGender(c.gender, lang)}</p>
          <div className="flex items-center gap-2 mt-3 flex-wrap text-xs">
            <span className={c.plan?"text-[#3A7D44]":"text-[#E8A317]"}>● {c.plan ? (lang === "ar" ? "خطة نشطة" : "Active plan") : (lang === "ar" ? "قيد التقييم" : "Under assessment")}</span>
            {c.linked_cases_count > 0 && <span className="px-2 py-0.5 rounded-full bg-[#769FCD]/15 text-[#527598] inline-flex items-center gap-1" data-testid={`linked-cases-${c.id}`}>
              <Users size={11}/>{c.linked_cases_count} {lang === "ar" ? "حالة دمج" : "inclusion case(s)"}
            </span>}
          </div>
        </Card></Link>)}
        {list.length === 0 && <div className="md:col-span-2 lg:col-span-3 text-center py-12 text-[#4A5553] text-sm">{t("children.empty")}</div>}
      </div>
    </div>
  );
}

// ============= GOAL CHART =============
function GoalChart({ goalId }) {
  const [data, setData] = useState([]);
  useEffect(() => { axios.get(`${API}/goals/${goalId}/progress`).then(r => {
    setData(r.data.map((d,i) => ({ idx: `جلسة ${i+1}`, rate: d.success_rate })));
  }); }, [goalId]);
  if (data.length === 0) return <p className="text-xs text-[#4A5553] italic">لا توجد جلسات مسجلة بعد</p>;
  return <ResponsiveContainer width="100%" height={140}>
    <LineChart data={data} margin={{top:5,right:10,left:10,bottom:5}}>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DB"/>
      <XAxis dataKey="idx" reversed tick={{fontSize:11,fill:"#4A5553"}}/>
      <YAxis domain={[0,100]} orientation="right" tick={{fontSize:11,fill:"#4A5553"}}/>
      <Tooltip contentStyle={{background:"#fff",border:"1px solid #E5E2DB",borderRadius:"8px",direction:"rtl"}} formatter={(v)=>[`${v}%`,"نسبة النجاح"]}/>
      <Line type="monotone" dataKey="rate" stroke="#2F5233" strokeWidth={2.5} dot={{fill:"#2F5233",r:4}}/>
    </LineChart>
  </ResponsiveContainer>;
}

// ============= CHILD DETAIL (Assessment + Plan + Certificate) =============
function ParentCodeCard({ child, onRegenerated }) {
  const [copied, setCopied] = useState("");
  const [busy, setBusy] = useState(false);
  const [showQR, setShowQR] = useState(true);
  const code = child?.access_code || "";
  const accessUrl = `${window.location.origin}/access?code=${code}`;
  const qrRef = useRef(null);
  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(()=>{ setCopied(key); setTimeout(()=>setCopied(""), 1500); });
  };
  const regen = async () => {
    if (!window.confirm("سيُلغى الكود الحالي ويُنشأ كود جديد. تأكد من إرسال الكود الجديد لولي الأمر.")) return;
    setBusy(true);
    try { await axios.post(`${API}/children/${child.id}/regenerate-code`); onRegenerated(); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const downloadQR = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    canvas.width = 1024; canvas.height = 1024;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 1024, 1024);
      ctx.drawImage(img, 32, 32, 960, 960);
      URL.revokeObjectURL(url);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `qr-${child.name || "child"}-${code}.png`;
      a.click();
    };
    img.src = url;
  };
  const printCard = () => {
    const svg = qrRef.current?.querySelector("svg");
    const svgHtml = svg ? svg.outerHTML : "";
    const body = `
      <div class="section" style="text-align:center">
        <h3 style="justify-content:center">بطاقة دخول ولي الأمر</h3>
        <p style="color:#4A5553;font-size:13px;margin:6px 0 18px">امسح الرمز بكاميرا الجوال أو أدخل الكود يدوياً — لا حاجة لإنشاء حساب.</p>
        <div style="display:inline-block;padding:14px;background:#fff;border:2px solid #2F5233;border-radius:12px">${svgHtml.replace(/width="\d+"/,'width="240"').replace(/height="\d+"/,'height="240"')}</div>
        <div style="margin-top:22px;font-family:'Courier New',monospace;font-size:34px;font-weight:800;color:#2F5233;letter-spacing:10px;background:#F0EFEB;padding:14px 20px;border-radius:10px;display:inline-block">${code}</div>
        <p style="font-size:11px;color:#4A5553;word-break:break-all;margin-top:14px">${accessUrl}</p>
      </div>
      <div class="section">
        <h3>تعليمات الدخول</h3>
        <ul>
          <li>افتح موقع Inclusion+AI ثم اختر «ولي أمر».</li>
          <li>أدخل الكود المكوّن من الأحرف والأرقام أعلاه، أو امسح الـ QR.</li>
          <li>الكود سرّي — لا تشاركه مع أي شخص خارج الأسرة أو الفريق المعتمد.</li>
        </ul>
      </div>`;
    printPremium({
      title: "بطاقة دخول ولي الأمر",
      subtitle: "Parent Access Card",
      docType: "بطاقة رسمية",
      subjectName: child.name,
      subjectMeta: { "العمر": `${child.age} سنة`, "الكود": code },
      body,
      signatures: null,
      refPrefix: "PA",
      showSeal: true,
    });
  };

  return <Card className="border-2 border-[#769FCD]/40 bg-[#769FCD]/5" data-testid="parent-code-card">
    <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
      <h2 className="font-semibold flex items-center gap-2 text-[#527598]"><Share2 size={16}/>كود ولي الأمر</h2>
      <div className="flex gap-2 flex-wrap">
        <Btn variant="ghost" onClick={()=>setShowQR(s=>!s)} data-testid="toggle-qr">{showQR?"إخفاء QR":"إظهار QR"}</Btn>
        <Btn variant="ghost" onClick={regen} disabled={busy} data-testid="regen-code"><RefreshCw size={12} className="inline ms-1"/>تجديد</Btn>
      </div>
    </div>
    <p className="text-xs text-[#4A5553] mb-3">يستطيع ولي أمر هذا الطالب الدخول مباشرة بدون إنشاء حساب — استخدم QR أو الكود أو الرابط.</p>
    <div className={`grid ${showQR && code ? "md:grid-cols-2" : "grid-cols-1"} gap-3 items-start`}>
      {showQR && code && <div className="bg-white p-3 rounded-lg border border-[#E5E2DB] text-center" ref={qrRef}>
        <div className="inline-block p-2 bg-white" data-testid="qr-code">
          <QRCodeSVG value={accessUrl} size={180} level="M" includeMargin={false} fgColor="#2F5233" bgColor="#ffffff"/>
        </div>
        <p className="text-xs text-[#4A5553] mt-2">مسح بكاميرا الجوال</p>
        <div className="flex gap-2 mt-2 justify-center flex-wrap">
          <Btn variant="outline" onClick={downloadQR} data-testid="download-qr"><FileDown size={12} className="inline ms-1"/>تنزيل PNG</Btn>
          <Btn variant="outline" onClick={printCard} data-testid="print-card"><FileDown size={12} className="inline ms-1"/>طباعة البطاقة</Btn>
        </div>
      </div>}
      <div className="space-y-2">
        <div className="flex gap-2 items-center bg-white p-2 rounded-lg border border-[#E5E2DB]">
          <span className="text-2xl font-mono font-bold text-[#2F5233] tracking-widest flex-1 text-center" data-testid="access-code-display">{code || "—"}</span>
          <Btn variant="outline" onClick={()=>copy(code, "code")} disabled={!code} data-testid="copy-code">{copied==="code"?<><Check size={12} className="inline ms-1"/>تم</>:<><Copy size={12} className="inline ms-1"/>نسخ</>}</Btn>
        </div>
        <div className="flex gap-2 items-center text-xs">
          <span className="flex-1 px-2 py-1.5 bg-white rounded border border-[#E5E2DB] text-[#4A5553] truncate font-mono">{accessUrl}</span>
          <Btn variant="outline" onClick={()=>copy(accessUrl, "url")} disabled={!code} data-testid="copy-url">{copied==="url"?"تم":"نسخ الرابط"}</Btn>
        </div>
      </div>
    </div>
  </Card>;
}

// ============= LINKED CASES SECTION (Professional Linking System v3.0) =============
function LinkedCasesSection({ child, onCreated, onChange }) {
  const { user } = useAuth();
  const acct = user?.account_type || "parent";
  const [show, setShow] = useState(false);
  const [meta, setMeta] = useState({ support_categories: [], inclusion_settings: [], communication_levels: [], support_levels: [] });
  const [form, setForm] = useState({ grade_class: "", inclusion_setting: "general_education", communication_level: "", support_level: "", support_categories: [] });
  const [busy, setBusy] = useState(false);
  const cases = child.linked_cases || [];
  // Only the child owner (usually the inclusion_head/parent who registered the child) can create cases
  const canCreate = acct !== "specialist";
  // Backend returns support_categories as [{key, label_ar}, ...]; build a lookup
  const supportLookup = (meta.support_categories || []).reduce((acc, c) => { acc[c.key] = c.label_ar; return acc; }, {});

  useEffect(() => {
    if (canCreate) {
      axios.get(`${API}/cases/meta`).then(r => setMeta(r.data)).catch(() => {});
    }
  }, [canCreate]);

  const toggleCategory = (k) => setForm(f => ({
    ...f,
    support_categories: f.support_categories.includes(k)
      ? f.support_categories.filter(x => x !== k)
      : [...f.support_categories, k]
  }));

  const createLinked = async (e) => {
    e.preventDefault();
    if (form.support_categories.length === 0) { alert("اختر فئة دعم واحدة على الأقل"); return; }
    setBusy(true);
    try {
      const payload = {
        child_id: child.id, name: child.name, age: child.age,
        grade_class: form.grade_class,
        inclusion_setting: form.inclusion_setting,
        communication_level: form.communication_level,
        support_level: form.support_level,
        support_categories: form.support_categories,
      };
      const { data } = await axios.post(`${API}/cases`, payload);
      onChange && onChange();
      onCreated && onCreated(data.id);
    } catch (err) {
      alert(fmtErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const statusLabel = (s) => ({ active: "نشطة", paused: "معلّقة", closed: "مكتملة" }[s] || s || "نشطة");
  const settingLabel = (s) => ({
    general_education: "تعليم عام شامل",
    inclusion_partial: "دمج جزئي",
    resource_room: "غرفة مصادر",
    self_contained: "فصل خاص",
  }[s] || s || "—");

  return <Card data-testid="linked-cases-section" className="border-2 border-[#2F5233]/20">
    <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
      <div>
        <h2 className="text-xl font-semibold font-heading flex items-center gap-2 text-[#2F5233]"><Users size={20}/>حالات الدمج المرتبطة بالطفل</h2>
        <p className="text-xs text-[#4A5553] mt-1">كل حالة دمج تُمثّل ملف تدخل متعدد التخصصات لهذا الطفل. الطفل هو الكيان المركزي للنظام.</p>
      </div>
      {canCreate && !show && cases.length === 0 && <Btn onClick={()=>setShow(true)} data-testid="open-linked-case-btn"><Plus size={14} className="inline ms-1"/>إنشاء حالة دمج مرتبطة</Btn>}
      {canCreate && !show && cases.length > 0 && <Btn variant="outline" onClick={()=>setShow(true)} data-testid="open-linked-case-btn"><Plus size={14} className="inline ms-1"/>حالة دمج إضافية</Btn>}
    </div>

    {show && canCreate && <form onSubmit={createLinked} className="p-4 bg-[#F9F8F6] rounded-lg space-y-3 mb-4" data-testid="linked-case-form">
      <div className="text-xs text-[#4A5553] bg-white p-2 rounded border border-[#E5E2DB]">
        <strong>الطفل المرتبط:</strong> {child.name} · {child.age} سنة · {child.gender}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input placeholder="الصف / المرحلة" value={form.grade_class} onChange={e=>setForm({...form, grade_class:e.target.value})} data-testid="lc-grade"/>
        <select className="w-full px-3 py-2.5 rounded-lg border border-[#E5E2DB] bg-white" value={form.inclusion_setting} onChange={e=>setForm({...form, inclusion_setting:e.target.value})} data-testid="lc-setting">
          <option value="general_education">تعليم عام شامل</option>
          <option value="inclusion_partial">دمج جزئي</option>
          <option value="resource_room">غرفة مصادر</option>
          <option value="self_contained">فصل خاص</option>
        </select>
        <select className="w-full px-3 py-2.5 rounded-lg border border-[#E5E2DB] bg-white" value={form.communication_level} onChange={e=>setForm({...form, communication_level:e.target.value})} data-testid="lc-comm">
          <option value="">مستوى التواصل (اختياري)</option>
          <option value="verbal">لفظي مستقل</option>
          <option value="partial_verbal">لفظي جزئي</option>
          <option value="non_verbal">غير لفظي</option>
          <option value="PECS">PECS صور</option>
          <option value="signs">إشارات</option>
        </select>
        <select className="w-full px-3 py-2.5 rounded-lg border border-[#E5E2DB] bg-white" value={form.support_level} onChange={e=>setForm({...form, support_level:e.target.value})} data-testid="lc-support">
          <option value="">مستوى الدعم (اختياري)</option>
          <option value="level1">المستوى 1 — دعم بسيط</option>
          <option value="level2">المستوى 2 — دعم ملحوظ</option>
          <option value="level3">المستوى 3 — دعم مكثّف</option>
        </select>
      </div>
      <div>
        <p className="text-xs text-[#4A5553] mb-2 font-medium">فئات الدعم (اختر واحدة أو أكثر) *</p>
        <div className="flex flex-wrap gap-2">{(meta.support_categories || []).map(c => {
          const active = form.support_categories.includes(c.key);
          return <button type="button" key={c.key} onClick={()=>toggleCategory(c.key)} data-testid={`lc-cat-${c.key}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${active?"bg-[#2F5233] text-white border-[#2F5233]":"bg-white text-[#4A5553] border-[#E5E2DB] hover:border-[#2F5233]"}`}>
            {c.label_ar}
          </button>;
        })}</div>
      </div>
      <div className="flex gap-2 pt-1">
        <Btn type="submit" disabled={busy} data-testid="lc-submit">{busy ? "جارٍ الإنشاء..." : "إنشاء وفتح ملف الحالة"}</Btn>
        <Btn variant="outline" type="button" onClick={()=>setShow(false)}>إلغاء</Btn>
      </div>
    </form>}

    {cases.length === 0 && !show ? <div className="text-center py-8 text-sm text-[#4A5553] bg-[#F9F8F6] rounded-lg">
      <p>لا توجد حالات دمج مرتبطة بهذا الطفل بعد.</p>
      {canCreate && <p className="text-xs mt-1">أنشئ حالة لربط فريق متعدد التخصصات والتقييمات وخطة التدخل بهذا الطفل.</p>}
    </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {cases.map(k => <Link key={k.id} to={`/cases/${k.id}`} data-testid={`linked-case-${k.id}`}>
        <div className="p-3 rounded-lg border border-[#E5E2DB] hover:border-[#2F5233] transition bg-white">
          <div className="flex justify-between items-start gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold truncate">{k.name}</h4>
              <p className="text-xs text-[#4A5553] mt-0.5">{settingLabel(k.inclusion_setting)} · {k.team_size || 0} عضو</p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${k.status==="active"?"bg-emerald-100 text-emerald-700":"bg-[#F0EFEB] text-[#4A5553]"}`}>{statusLabel(k.status)}</span>
          </div>
          {k.support_categories?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">
            {k.support_categories.slice(0, 3).map((s) => <span key={s} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#769FCD]/15 text-[#527598]">{supportLookup[s] || s}</span>)}
            {k.support_categories.length > 3 && <span className="text-[10px] text-[#4A5553]">+{k.support_categories.length - 3}</span>}
          </div>}
        </div>
      </Link>)}
    </div>}
  </Card>;
}

function ChildDetail() {
  const id = window.location.pathname.split("/").pop();
  const nav = useNavigate();
  const [child, setChild] = useState(null); const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({ eye_contact:"", verbal:"", social:"", repetitive:"", sensory:"" });
  const [progress, setProgress] = useState({ goalId:"", note:"", rate:50 });
  const [cert, setCert] = useState(null);
  const [showChart, setShowChart] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const load = () => axios.get(`${API}/children/${id}`).then(r => setChild(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => { setLoading(true);
    try { await axios.post(`${API}/assessment`, { child_id: id, answers }); await load(); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  const logProg = async (gid) => {
    await axios.post(`${API}/goals/progress`, { goal_id: gid, note: progress.note || "جلسة تدريب", success_rate: progress.rate });
    setProgress({ goalId:"", note:"", rate:50 }); load();
  };
  const getCert = async (gid) => {
    try { const { data } = await axios.get(`${API}/certificate/${id}/${gid}`); setCert(data); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); }
  };
  if (!child) return <div>...</div>;
  return (
    <div className="space-y-6 fade-up" data-testid="child-detail">
      <div><h1 className="text-4xl font-bold font-heading">{child.name}</h1><p className="text-[#4A5553]">{child.age} سنة · {child.gender}</p></div>
      <ParentCodeCard child={child} onRegenerated={load}/>
      <LinkedCasesSection child={child} onCreated={(newCid) => nav(`/cases/${newCid}`)} onChange={load}/>
      {!child.plan ? <Card><h2 className="text-xl font-semibold mb-4 font-heading">التقييم المبدئي</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[["eye_contact","التواصل البصري"],["verbal","المهارات اللفظية"],["social","التفاعل الاجتماعي"],["repetitive","السلوكيات المتكررة"],["sensory","الحساسية الحسية"]].map(([k,l]) =>
            <div key={k}><label className="block text-sm mb-1">{l}</label>
              <select data-testid={`ans-${k}`} value={answers[k]} onChange={e=>setAnswers({...answers,[k]:e.target.value})} className="w-full px-4 py-2.5 rounded-lg border border-[#E5E2DB] bg-white">
                <option value="">اختر</option><option value="ضعيف">ضعيف</option><option value="متوسط">متوسط</option><option value="جيد">جيد</option></select></div>)}
        </div>
        <Btn data-testid="gen-plan-btn" onClick={generate} disabled={loading} className="mt-6">{loading?"جارٍ التوليد بـ GPT-5.2...":"توليد الخطة بالذكاء الاصطناعي"}</Btn>
      </Card> :
      <>
        <div className="flex justify-end gap-2 flex-wrap">
          <Btn variant="outline" data-testid="share-plan-btn" onClick={()=>setShareOpen(true)}><Share2 size={16} className="inline ms-1"/>مشاركة الخطة</Btn>
          <Btn variant="outline" data-testid="print-plan-pdf" onClick={()=>printPlan(child, child.plan)}><Printer size={16} className="inline ms-1"/>طباعة</Btn>
          <Btn variant="primary" data-testid="export-plan-pdf" onClick={()=>downloadPlan(child, child.plan)}><FileDown size={16} className="inline ms-1"/>تنزيل PDF فاخر</Btn>
        </div>
        {shareOpen && <ShareModal childId={id} childName={child.name} onClose={()=>setShareOpen(false)}/>}
        <div id="plan-export-area" className="space-y-6 bg-white">
        <Card><h2 className="text-xl font-semibold mb-3 font-heading">ملخص الحالة - {child.name}</h2>
          <p className="text-xs text-[#4A5553] mb-3">{child.age} سنة · {child.gender}</p>
          <p className="text-[#4A5553] leading-relaxed">{child.plan.summary}</p>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div><p className="font-semibold mb-2">نقاط القوة</p><ul className="list-disc ps-5 text-sm text-[#4A5553]">{child.plan.strengths?.map((s,i)=><li key={i}>{s}</li>)}</ul></div>
            <div><p className="font-semibold mb-2">التحديات</p><ul className="list-disc ps-5 text-sm text-[#4A5553]">{child.plan.challenges?.map((s,i)=><li key={i}>{s}</li>)}</ul></div>
          </div>
          {child.plan.weekly_schedule && <div className="mt-4"><p className="font-semibold mb-2">الجدول الأسبوعي</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">{child.plan.weekly_schedule.map((w,i)=><div key={i} className="p-2 bg-[#F0EFEB] rounded"><strong>{w.day}:</strong> {w.activity}</div>)}</div></div>}
          {child.plan.parent_tips && <div className="mt-4"><p className="font-semibold mb-2">نصائح للأهل</p><ul className="list-disc ps-5 text-sm text-[#4A5553]">{child.plan.parent_tips.map((s,i)=><li key={i}>{s}</li>)}</ul></div>}
        </Card>
        <Card data-testid="goals-section"><h2 className="text-xl font-semibold mb-4 font-heading">الأهداف التأهيلية</h2>
          <div className="space-y-4">{child.plan.goals?.map(g => <div key={g.id} className="p-4 rounded-lg border border-[#E5E2DB]">
            <div className="flex justify-between items-start gap-3"><div><h3 className="font-semibold">{g.title}</h3><p className="text-xs text-[#4A5553] mt-1">{g.domain} · {g.duration_weeks} أسابيع · {g.techniques?.join(", ")}</p></div>
              <span className={`px-3 py-1 rounded-full text-xs ${g.progress>=80?"bg-[#3A7D44]/10 text-[#3A7D44]":"bg-[#E8A317]/10 text-[#E8A317]"}`}>{g.progress||0}%</span></div>
            <p className="text-sm mt-2"><strong>المعزز:</strong> {g.reinforcement}</p>
            <div className="mt-3 bg-[#F0EFEB] rounded-full h-2"><div className="bg-[#2F5233] h-2 rounded-full transition-all" style={{width:`${g.progress||0}%`}}/></div>
            <div className="mt-3 flex gap-2 flex-wrap items-center">
              <input data-testid={`prog-rate-${g.id}`} type="number" min="0" max="100" placeholder="%" className="w-20 px-2 py-1 border border-[#E5E2DB] rounded" onChange={e=>setProgress(p=>({...p,rate:parseInt(e.target.value)||0,goalId:g.id}))}/>
              <Btn variant="secondary" data-testid={`log-prog-${g.id}`} onClick={()=>logProg(g.id)}>تسجيل تقدم</Btn>
              <Btn variant="outline" data-testid={`chart-toggle-${g.id}`} onClick={()=>setShowChart(s=>({...s,[g.id]:!s[g.id]}))}><BarChart3 size={16} className="inline ms-1"/>{showChart[g.id]?"إخفاء":"مخطط"}</Btn>
              {g.progress>=80 && <Btn data-testid={`cert-${g.id}`} onClick={()=>getCert(g.id)}><Award size={16} className="inline ms-1"/>شهادة</Btn>}
            </div>
            {showChart[g.id] && <div className="mt-4 p-3 bg-[#F9F8F6] rounded-lg" data-testid={`chart-${g.id}`}><GoalChart goalId={g.id}/></div>}
          </div>)}</div></Card>
        <Card data-testid="plan-overview-chart"><h2 className="text-xl font-semibold mb-4 font-heading">نظرة عامة على تقدم الأهداف</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={child.plan.goals?.map(g=>({name:g.title.slice(0,20),progress:g.progress||0}))} margin={{top:10,right:10,left:10,bottom:30}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DB"/>
              <XAxis dataKey="name" tick={{fontSize:10,fill:"#4A5553"}} angle={-20} textAnchor="end" height={60}/>
              <YAxis domain={[0,100]} orientation="right" tick={{fontSize:11,fill:"#4A5553"}}/>
              <Tooltip contentStyle={{background:"#fff",border:"1px solid #E5E2DB",borderRadius:"8px",direction:"rtl"}} formatter={(v)=>[`${v}%`,"التقدم"]}/>
              <Bar dataKey="progress" fill="#769FCD" radius={[6,6,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        </div>
        <ScreeningHistory childId={id} />
        <GamesProgressSection childId={id} />
      </>}
      {cert && <>
        <div className="flex justify-end gap-2 no-print">
          <Btn variant="outline" data-testid="print-cert" onClick={()=>printCertificate(cert)}><Printer size={16} className="inline ms-1"/>طباعة</Btn>
          <Btn variant="primary" data-testid="export-cert-pdf" onClick={()=>downloadCertificate(cert)}><FileDown size={16} className="inline ms-1"/>تنزيل PDF فاخر</Btn>
        </div>
        <Card id="cert-export-area" className="border-2 border-[#D4AF37] text-center bg-gradient-to-br from-[#FFFEF9] to-[#FAF8EE]" data-testid="certificate">
          <div className="text-[#D4AF37] text-3xl">❖</div>
          <h2 className="text-3xl font-bold mt-2 font-heading text-[#2F5233]" style={{fontFamily:"serif"}}>شهادة إنجاز رسمية</h2>
          <p className="text-xs text-[#B8860B] tracking-widest mt-1">CERTIFICATE OF ACHIEVEMENT</p>
          <div className="my-6 border-y border-[#D4AF37]/30 py-4">
            <p className="text-[#4A5553]">تُمنح هذه الشهادة إلى</p>
            <p className="text-4xl font-bold text-[#2F5233] my-3" style={{fontFamily:"serif"}}>{cert.child_name}</p>
            <p className="text-[#4A5553]">لإنجازه الهدف:</p>
            <p className="text-lg font-semibold text-[#1A201E] mt-1">{cert.goal_title}</p>
            <p className="text-sm mt-3">بنسبة نجاح <strong className="text-[#2F5233]">{cert.success_rate}%</strong></p>
          </div>
          <p className="text-xs text-[#4A5553]">المعالج: {cert.therapist} · رقم الشهادة: {cert.certificate_id}</p>
          <p className="text-xs text-[#4A5553] mt-1">تاريخ الإصدار: {fmtDate(cert.issued_at)}</p>
        </Card></>}
    </div>
  );
}

// ============= SCREENING HISTORY (per child) =============
function GamesProgressSection({ childId }) {
  const [badges, setBadges] = useState([]);
  useEffect(() => { axios.get(`${API}/games/catalog/badges`).then(r => setBadges(r.data)); }, []);
  return <ChildGamesProgress childId={childId} badges={badges}/>;
}
// ============= SCREENING HISTORY-LIST (per child) =============
function ScreeningHistory({ childId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    axios.get(`${API}/screening/history/${childId}`).then(r => setItems(r.data));
  }, [childId]);
  if (items.length === 0) return null;
  return <Card data-testid="screening-history">
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-xl font-semibold font-heading flex items-center gap-2"><ListVideo size={20}/>سجل الفحوصات السابقة</h2>
      <span className="text-sm text-[#4A5553]">{items.length} جلسة</span>
    </div>
    <div className="space-y-2">{items.map(s => <div key={s.id} className="border border-[#E5E2DB] rounded-lg" data-testid={`hist-session-${s.id}`}>
      <button onClick={()=>setOpen(open===s.id?null:s.id)} className="w-full p-3 flex justify-between items-center hover:bg-[#F9F8F6] rounded-lg text-start">
        <div>
          <p className="font-medium text-sm">{fmtDate(s.created_at)}</p>
          <p className="text-xs text-[#4A5553] mt-1">{s.frames_count} إطار · تواصل بصري {Math.round(s.avg_eye_contact)}% · تفاعل {Math.round(s.avg_face_engagement)}%</p>
        </div>
        <span className={`px-2 py-1 rounded text-xs ${s.report?.risk_level==="مرتفع"?"bg-red-100 text-red-700":s.report?.risk_level==="متوسط"?"bg-amber-100 text-amber-700":"bg-green-100 text-green-700"}`}>{s.report?.risk_level||"-"}</span>
      </button>
      {open===s.id && <div className="p-4 bg-[#F9F8F6] border-t border-[#E5E2DB] text-sm space-y-2">
        <p><strong>الدرجة الإجمالية:</strong> {s.report?.overall_score}/100</p>
        {s.report?.observations && <div><strong>الملاحظات:</strong><ul className="list-disc ps-5 mt-1 text-[#4A5553]">{s.report.observations.map((o,i)=><li key={i}>{o}</li>)}</ul></div>}
        {s.report?.recommendations && <div><strong>التوصيات:</strong><ul className="list-disc ps-5 mt-1 text-[#4A5553]">{s.report.recommendations.map((o,i)=><li key={i}>{o}</li>)}</ul></div>}
        <p className="text-xs text-[#D9534F]">{s.report?.disclaimer}</p>
      </div>}
    </div>)}</div>
  </Card>;
}

// ============= LIVE SCREENING =============
function Screening() {
  const { t, lang } = useT();
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState("");
  const [running, setRunning] = useState(false);
  const [sessionId] = useState(() => Date.now().toString());
  const [frames, setFrames] = useState([]);
  const [report, setReport] = useState(null);
  const videoRef = useRef(null); const streamRef = useRef(null); const intervalRef = useRef(null);

  useEffect(() => { axios.get(`${API}/children`).then(r => setChildren(r.data)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    if (!childId) { alert(lang === "ar" ? "اختر طفلاً" : "Select a child"); return; }
    const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 }, audio: false });
    streamRef.current = stream; videoRef.current.srcObject = stream; setRunning(true); setReport(null); setFrames([]);
    intervalRef.current = setInterval(captureFrame, 4000);
  };
  const captureFrame = async () => {
    const v = videoRef.current; if (!v || v.videoWidth === 0) return;
    const c = document.createElement("canvas"); c.width = 320; c.height = 240;
    c.getContext("2d").drawImage(v, 0, 0, 320, 240);
    const b64 = c.toDataURL("image/jpeg", 0.7).split(",")[1];
    try { const { data } = await axios.post(`${API}/screening/analyze-frame`, { child_id: childId, session_id: sessionId, image_base64: b64 });
      setFrames(f => [...f, data]);
    } catch(e){ /* frame skip */ }
  };
  const stop = async () => {
    clearInterval(intervalRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop()); setRunning(false);
    try { const { data } = await axios.post(`${API}/screening/end-session`, { child_id: childId, session_id: sessionId });
      setReport(data.report);
    } catch(e){ alert(fmtErr(e.response?.data?.detail)); }
  };
  const last = frames[frames.length-1];
  return (
    <div className="space-y-6 fade-up" data-testid="screening-page">
      <h1 className="text-4xl font-bold font-heading">{t("page.screening")}</h1>
      <p className="text-[#4A5553]">{lang === "ar" ? "يلتقط النظام إطاراً كل 4 ثوانٍ ويحلله بـ Gemini Vision. التقرير النهائي بـ GPT-5.2." : "The system captures a frame every 4 seconds and analyzes it with Gemini Vision. Final report by GPT-5.2."}</p>
      <Card>
        <div className="flex gap-3 items-center flex-wrap">
          <select data-testid="select-child" value={childId} onChange={e=>setChildId(e.target.value)} disabled={running} className="px-4 py-2.5 rounded-lg border border-[#E5E2DB] bg-white">
            <option value="">{lang === "ar" ? "اختر الطفل" : "Select child"}</option>{children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          {!running ? <Btn data-testid="start-scan" onClick={start}><Play size={16} className="inline ms-1"/>{lang === "ar" ? "بدء" : "Start"}</Btn>
                   : <Btn variant="secondary" data-testid="stop-scan" onClick={stop}><Square size={16} className="inline ms-1"/>{lang === "ar" ? "إنهاء وتقرير" : "End & report"}</Btn>}
        </div>
      </Card>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-[#1A201E] rounded-xl overflow-hidden relative aspect-video">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover"/>
          {running && <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/50 text-white px-3 py-1 rounded-full"><span className="w-2 h-2 bg-red-500 rounded-full scan-pulse"/>{lang === "ar" ? "تحليل مباشر" : "Live analysis"}</div>}
          {!running && !streamRef.current && <div className="absolute inset-0 flex items-center justify-center text-white/60"><Camera size={48}/></div>}
        </div>
        <Card>
          <h3 className="font-semibold mb-3 font-heading">{lang === "ar" ? "القياسات المباشرة" : "Live measurements"}</h3>
          {last ? <div className="space-y-3">
            {[["eye_contact", lang === "ar" ? "التواصل البصري" : "Eye contact"],["facial_engagement", lang === "ar" ? "التفاعل التعبيري" : "Facial engagement"]].map(([k,l])=>
              <div key={k}><div className="flex justify-between text-sm mb-1"><span>{l}</span><span>{last[k]}%</span></div>
                <div className="bg-[#F0EFEB] h-2 rounded-full"><div className="bg-[#769FCD] h-2 rounded-full transition-all" style={{width:`${last[k]}%`}}/></div></div>)}
            <p className="text-sm pt-2"><strong>{lang === "ar" ? "الحالة:" : "State:"}</strong> {last.affect} · <strong>{lang === "ar" ? "النظر:" : "Gaze:"}</strong> {last.gaze_direction}</p>
            <p className="text-xs text-[#4A5553]">{last.notes_ar}</p>
            <p className="text-xs text-[#4A5553] mt-2">{lang === "ar" ? "عدد الإطارات المحللة:" : "Frames analyzed:"} {frames.length}</p>
          </div> : <p className="text-[#4A5553] text-sm">{lang === "ar" ? "ابدأ الجلسة لرؤية التحليل المباشر" : "Start the session to see live analysis"}</p>}
        </Card>
      </div>
      {report && <>
        <div className="flex justify-end gap-2 no-print">
          <Btn variant="outline" data-testid="print-screening" onClick={()=>printScreeningReport(report)}><Printer size={16} className="inline ms-1"/>{lang === "ar" ? "طباعة" : "Print"}</Btn>
          <Btn variant="primary" data-testid="export-screening-pdf" onClick={()=>downloadScreeningReport(report)}><FileDown size={16} className="inline ms-1"/>{lang === "ar" ? "تنزيل PDF فاخر" : "Download premium PDF"}</Btn>
        </div>
        <Card id="screening-report-area" data-testid="screening-report" className="border-2 border-[#769FCD]">
        <h2 className="text-xl font-bold mb-3 font-heading">{lang === "ar" ? "تقرير الفحص المبدئي" : "Initial screening report"}</h2>
        <p className="text-xs text-[#4A5553] mb-3">{lang === "ar" ? "تاريخ الإصدار:" : "Issued on:"} {new Date().toLocaleString(lang === "ar" ? "ar" : "en-US")}</p>
        <div className="flex gap-4 items-center mb-4"><div className="text-4xl font-bold text-[#2F5233]">{report.overall_score}/100</div>
          <div className={`px-3 py-1 rounded-full text-sm ${report.risk_level==="مرتفع"?"bg-red-100 text-red-700":report.risk_level==="متوسط"?"bg-amber-100 text-amber-700":"bg-green-100 text-green-700"}`}>{lang === "ar" ? "مستوى الخطر:" : "Risk level:"} {report.risk_level}</div></div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><p className="font-semibold mb-2">{lang === "ar" ? "الملاحظات" : "Observations"}</p><ul className="list-disc ps-5 text-sm text-[#4A5553] space-y-1">{report.observations?.map((o,i)=><li key={i}>{o}</li>)}</ul></div>
          <div><p className="font-semibold mb-2">{lang === "ar" ? "التوصيات" : "Recommendations"}</p><ul className="list-disc ps-5 text-sm text-[#4A5553] space-y-1">{report.recommendations?.map((o,i)=><li key={i}>{o}</li>)}</ul></div>
        </div>
        <p className="text-xs text-[#D9534F] mt-4 font-medium">{report.disclaimer}</p>
      </Card></>}
    </div>
  );
}

// ============= ADVISOR CHAT =============
const INIT_MSG_AR = { id: "init", role:"assistant", content:"أهلاً! أنا مستشارك الذكي في مجال الدمج التعليمي والتربية. كيف أقدر أساعدك اليوم؟" };
const INIT_MSG_EN = { id: "init", role:"assistant", content:"Hi! I'm your AI advisor for inclusive education and parenting. How can I help you today?" };
const INIT_MSG = INIT_MSG_AR;
function Advisor() {
  const { t, lang } = useT();
  const INIT_MSG_DYN = lang === "ar" ? INIT_MSG_AR : INIT_MSG_EN;
  const [sessionId, setSessionId] = useState(() => "s-" + Date.now());
  const [msgs, setMsgs] = useState([INIT_MSG_DYN]);
  const [txt, setTxt] = useState(""); const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef(null);

  // re-init greeting when lang changes if the chat is at its initial state
  useEffect(() => {
    setMsgs(prev => (prev.length === 1 && prev[0].id === "init") ? [INIT_MSG_DYN] : prev);
  }, [lang, INIT_MSG_DYN]);

  const loadSessions = () => axios.get(`${API}/advisor/sessions`).then(r => setSessions(r.data));
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo(0, 99999); }, [msgs]);

  const openSession = async (sid) => {
    setSessionId(sid); setShowHistory(false);
    const { data } = await axios.get(`${API}/advisor/history/${sid}`);
    setMsgs(data.length ? data.map((m,i)=>({ id: m.id || `m-${i}`, role: m.role, content: m.content })) : [INIT_MSG_DYN]);
  };
  const newChat = () => {
    setSessionId("s-" + Date.now()); setMsgs([INIT_MSG_DYN]); setShowHistory(false);
    loadSessions();
  };
  const send = async () => {
    if (!txt.trim() || busy) return;
    const m = txt.trim(); setTxt(""); setMsgs(x=>[...x,{id:`u-${Date.now()}`,role:"user",content:m}]); setBusy(true);
    try { const { data } = await axios.post(`${API}/advisor/chat`, { session_id: sessionId, message: m });
      setMsgs(x=>[...x,{id:`a-${Date.now()}`,role:"assistant",content:data.reply}]);
      loadSessions();
    } catch(e){ setMsgs(x=>[...x,{id:`err-${Date.now()}`,role:"assistant",content:t("advisor.connection_error")}]); } finally { setBusy(false); }
  };
  return (
    <div className="fade-up h-[calc(100vh-5rem)] flex flex-col" data-testid="advisor-page">
      <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-4xl font-bold font-heading mb-2">{t("advisor.title")}</h1>
          <p className="text-[#4A5553]">{t("advisor.subtitle_long")}</p>
        </div>
        <div className="flex gap-2">
          <Btn variant="outline" data-testid="new-chat-btn" onClick={newChat}><MessageSquarePlus size={16} className="inline ms-1"/>{t("advisor.new_chat")}</Btn>
          <Btn variant="outline" data-testid="history-btn" onClick={()=>setShowHistory(s=>!s)}><History size={16} className="inline ms-1"/>{t("advisor.history")} ({sessions.length})</Btn>
        </div>
      </div>
      <div className="flex-1 flex gap-4 overflow-hidden">
        {showHistory && <Card className="w-full md:w-72 overflow-y-auto p-0" data-testid="history-panel">
          <div className="p-3 border-b border-[#E5E2DB] sticky top-0 bg-white"><h3 className="font-semibold text-sm">{t("advisor.history_title")}</h3></div>
          <div>{sessions.length===0 ? <p className="p-4 text-sm text-[#4A5553]">{t("advisor.history_empty")}</p> :
            sessions.map(s => <button key={s.session_id} data-testid={`session-${s.session_id}`}
              onClick={()=>openSession(s.session_id)}
              className={`w-full text-start p-3 hover:bg-[#F0EFEB] border-b border-[#E5E2DB] transition ${s.session_id===sessionId?"bg-[#F0EFEB]":""}`}>
              <p className="text-sm truncate font-medium">{s.preview || t("advisor.empty_session")}</p>
              <p className="text-xs text-[#4A5553] mt-1">{fmtDate(s.last_ts)}</p>
            </button>)}
          </div>
        </Card>}
        <Card className="flex-1 flex flex-col overflow-hidden p-0">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
            {msgs.map((m) => <div key={m.id} className={`flex ${m.role==="user"?"justify-start":"justify-end"}`}>
              <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${m.role==="user"?"bg-[#2F5233] text-white":"bg-[#F0EFEB] text-[#1A201E]"}`}><p className="whitespace-pre-wrap leading-relaxed">{m.content}</p></div>
            </div>)}
            {busy && <div className="flex justify-end"><div className="bg-[#F0EFEB] px-4 py-3 rounded-2xl text-[#4A5553]">{t("advisor.typing")}</div></div>}
          </div>
          <div className="border-t border-[#E5E2DB] p-4 flex gap-2">
            <Input data-testid="chat-input" placeholder={t("advisor.placeholder_ask")} value={txt} onChange={e=>setTxt(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()}/>
            <Btn data-testid="chat-send" onClick={send} disabled={busy}><Send size={18}/></Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ============= SHARE MODAL =============
function ShareModal({ childId, childName, onClose }) {
  const { t } = useT();
  const [days, setDays] = useState(7);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/share/plan`, { child_id: childId, days_valid: days });
      const url = `${window.location.origin}/shared/${data.token}`;
      setLink({ url, expires_at: data.expires_at });
    } catch (e) { alert(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(link.url);
    setCopied(true); setTimeout(()=>setCopied(false), 2000);
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose} data-testid="share-modal">
      <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={e=>e.stopPropagation()}>
        <h3 className="text-xl font-bold font-heading mb-2">{t("share.title_plan")} {childName}</h3>
        <p className="text-sm text-[#4A5553] mb-4">{t("share.subtitle")}</p>
        {!link ? <>
          <label className="block text-sm mb-2">{t("share.days_label")}</label>
          <Input type="number" min={1} max={30} value={days} onChange={e=>setDays(parseInt(e.target.value)||7)} data-testid="share-days"/>
          <div className="flex gap-2 mt-5">
            <Btn onClick={generate} disabled={busy} data-testid="share-generate">{busy?"...":t("share.generate")}</Btn>
            <Btn variant="outline" onClick={onClose}>{t("common.cancel")}</Btn>
          </div>
        </> : <>
          <div className="bg-[#F0EFEB] p-3 rounded-lg text-sm break-all mb-3" data-testid="share-link">{link.url}</div>
          <p className="text-xs text-[#4A5553] mb-4">{t("share.valid_until")} {fmtDate(link.expires_at)}</p>
          <div className="flex gap-2">
            <Btn onClick={copy} data-testid="share-copy">{copied?<><Check size={16} className="inline ms-1"/>{t("share.copied")}</>:<><Copy size={16} className="inline ms-1"/>{t("share.copy")}</>}</Btn>
            <Btn variant="outline" onClick={onClose}>{t("share.close")}</Btn>
          </div>
        </>}
      </div>
    </div>
  );
}

// ============= CURRICULUM ADAPTATION (Goal Lifecycle) =============
const STATUS_LABELS = {
  draft:        { ar: "مسوّدة",       en: "Draft",         color: "bg-slate-100 text-slate-700" },
  active:       { ar: "نشط",          en: "Active",        color: "bg-[#769FCD]/15 text-[#527598]" },
  in_progress:  { ar: "قيد التنفيذ",  en: "In progress",   color: "bg-amber-100 text-amber-700" },
  under_review: { ar: "قيد المراجعة", en: "Under review",  color: "bg-purple-100 text-purple-700" },
  achieved:     { ar: "تم الإنجاز",   en: "Achieved",      color: "bg-[#3A7D44]/15 text-[#3A7D44]" },
  closed:       { ar: "مغلق",         en: "Closed",        color: "bg-emerald-200 text-emerald-900" },
  deferred:     { ar: "مؤجّل",         en: "Deferred",      color: "bg-orange-100 text-orange-700" },
};

function StatusBadge({ status, ...rest }) {
  const { lang } = useT();
  const s = STATUS_LABELS[status] || STATUS_LABELS.draft;
  return <span className={`px-2.5 py-1 rounded-full text-xs ${s.color}`} {...rest}>{s[lang] || s.ar}</span>;
}

function CurriculumList() {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    curriculum_name:"", grade_or_stage:"", subject:"", unit_or_section:"",
    lesson_title:"", original_objective:"", child_profile_reference:"",
    support_profile: {
      categories: ["autism"],
      severity: "خفيف",
      communication: "لفظي",
      sensory_needs: "",
      mobility_needs: "",
      cognitive_notes: "",
    },
  });
  const nav = useNavigate();

  // Support categories that the AI curriculum adapter handles (all disabilities — not autism only)
  const SUPPORT_CATEGORIES = [
    { id: "autism",              ar: "طيف التوحد",                    en: "Autism Spectrum" },
    { id: "adhd",                ar: "فرط الحركة وتشتت الانتباه",     en: "ADHD" },
    { id: "intellectual",        ar: "إعاقة ذهنية",                   en: "Intellectual Disability" },
    { id: "learning_difficulty", ar: "صعوبات التعلم",                  en: "Learning Difficulty" },
    { id: "physical",            ar: "إعاقة جسدية / حركية",           en: "Physical / Motor" },
    { id: "sensory_visual",      ar: "إعاقة بصرية",                    en: "Visual Impairment" },
    { id: "sensory_hearing",     ar: "إعاقة سمعية",                    en: "Hearing Impairment" },
    { id: "communication",       ar: "اضطرابات التواصل / النطق",       en: "Communication / Speech" },
    { id: "gifted_twice",        ar: "موهبة وصعوبة (Twice-exceptional)", en: "Twice-Exceptional" },
    { id: "other",               ar: "أخرى / متعددة",                  en: "Other / Multiple" },
  ];

  const load = async () => {
    const url = filter ? `${API}/adaptation/goals?status=${filter}` : `${API}/adaptation/goals`;
    const { data } = await axios.get(url);
    setItems(data);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/adaptation/generate`, form);
      setShow(false);
      nav(`/curriculum/${data.id}`);
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setSup = (k, v) => setForm(f => ({ ...f, support_profile: { ...f.support_profile, [k]: v } }));
  const toggleCategory = (id) => setForm(f => {
    const cur = f.support_profile.categories || [];
    const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
    return { ...f, support_profile: { ...f.support_profile, categories: next.length ? next : ["autism"] } };
  });

  return (
    <div className="space-y-6 fade-up" data-testid="curriculum-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold font-heading">{t("page.curriculum")}</h1>
          <p className="text-[#4A5553] mt-1">{t("page.curriculum_sub")}</p>
        </div>
        <Btn data-testid="new-adapt-btn" onClick={()=>setShow(true)}><Plus className="inline ms-1" size={16}/>{lang === "ar" ? "هدف جديد" : "New goal"}</Btn>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={()=>setFilter("")} data-testid="filter-all" className={`px-3 py-1.5 rounded-full text-sm ${!filter?"bg-[#2F5233] text-white":"bg-white border border-[#E5E2DB]"}`}>{lang === "ar" ? "الكل" : "All"} ({items.length})</button>
        {Object.entries(STATUS_LABELS).map(([k,v]) =>
          <button key={k} data-testid={`filter-${k}`} onClick={()=>setFilter(k)} className={`px-3 py-1.5 rounded-full text-sm ${filter===k?"bg-[#2F5233] text-white":"bg-white border border-[#E5E2DB]"}`}>{v[lang] || v.ar}</button>
        )}
      </div>

      {show && <Card data-testid="adapt-form-card">
        <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-semibold font-heading">{lang === "ar" ? "هدف تربوي جديد (لجميع الإعاقات)" : "New educational goal (all disabilities)"}</h2>
          <button onClick={()=>setShow(false)}><X size={20}/></button></div>
        <form onSubmit={generate} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="adapt-form">
          <Input placeholder={lang === "ar" ? "اسم المنهج (مثال: المنهج السعودي)" : "Curriculum (e.g., Saudi National Curriculum)"} value={form.curriculum_name} onChange={e=>setForm({...form,curriculum_name:e.target.value})} required data-testid="f-curriculum"/>
          <Input placeholder={lang === "ar" ? "المرحلة/الصف (مثال: الصف الثالث)" : "Stage/Grade (e.g., Grade 3)"} value={form.grade_or_stage} onChange={e=>setForm({...form,grade_or_stage:e.target.value})} required data-testid="f-grade"/>
          <Input placeholder={lang === "ar" ? "المادة (مثال: الرياضيات)" : "Subject (e.g., Mathematics)"} value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} required data-testid="f-subject"/>
          <Input placeholder={lang === "ar" ? "الوحدة/القسم" : "Unit / Section"} value={form.unit_or_section} onChange={e=>setForm({...form,unit_or_section:e.target.value})} required data-testid="f-unit"/>
          <Input placeholder={lang === "ar" ? "عنوان الدرس" : "Lesson title"} value={form.lesson_title} onChange={e=>setForm({...form,lesson_title:e.target.value})} required data-testid="f-lesson" className="md:col-span-2"/>
          <textarea placeholder={lang === "ar" ? "الهدف التعليمي الأصلي" : "Original learning objective"} value={form.original_objective} onChange={e=>setForm({...form,original_objective:e.target.value})} required data-testid="f-objective"
            className="md:col-span-2 w-full px-4 py-2.5 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 focus:border-[#2F5233] outline-none min-h-[80px]"/>
          <Input placeholder={lang === "ar" ? "معرّف/وصف الطفل (اختياري)" : "Child ID/description (optional)"} value={form.child_profile_reference} onChange={e=>setForm({...form,child_profile_reference:e.target.value})} data-testid="f-child-ref" className="md:col-span-2"/>

          <div className="md:col-span-2">
            <label className="block text-sm mb-2 font-medium">{lang === "ar" ? "فئات الدعم (اختر واحدة أو أكثر)" : "Support categories (select one or more)"}</label>
            <div className="flex flex-wrap gap-2" data-testid="f-support-categories">
              {SUPPORT_CATEGORIES.map(c => {
                const active = (form.support_profile.categories || []).includes(c.id);
                return (
                  <button key={c.id} type="button" data-testid={`sup-cat-${c.id}`} onClick={()=>toggleCategory(c.id)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition ${active ? "bg-[#2F5233] text-white border-[#2F5233]" : "bg-white border-[#E5E2DB] text-[#4A5553] hover:border-[#2F5233]"}`}>
                    {lang === "ar" ? c.ar : c.en}
                  </button>
                );
              })}
            </div>
          </div>

          <div><label className="block text-sm mb-1">{lang === "ar" ? "شدة الحاجة" : "Severity / Level"}</label>
            <select data-testid="f-sup-severity" value={form.support_profile.severity} onChange={e=>setSup("severity",e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-[#E5E2DB] bg-white">
              <option>{lang === "ar" ? "خفيف" : "Mild"}</option>
              <option>{lang === "ar" ? "متوسط" : "Moderate"}</option>
              <option>{lang === "ar" ? "شديد" : "Severe"}</option>
              <option>{lang === "ar" ? "شديد جداً" : "Profound"}</option>
            </select></div>
          <div><label className="block text-sm mb-1">{lang === "ar" ? "طريقة التواصل" : "Communication mode"}</label>
            <select data-testid="f-sup-comm" value={form.support_profile.communication} onChange={e=>setSup("communication",e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-[#E5E2DB] bg-white">
              <option>{lang === "ar" ? "لفظي" : "Verbal"}</option>
              <option>{lang === "ar" ? "غير لفظي" : "Non-verbal"}</option>
              <option>PECS</option>
              <option>{lang === "ar" ? "لغة إشارة" : "Sign language"}</option>
              <option>AAC</option>
              <option>{lang === "ar" ? "برايل" : "Braille"}</option>
            </select></div>

          <Input placeholder={lang === "ar" ? "احتياجات حسية (بصرية/سمعية/لمسية…) — اختياري" : "Sensory needs (visual/auditory/tactile…) — optional"} value={form.support_profile.sensory_needs} onChange={e=>setSup("sensory_needs",e.target.value)} data-testid="f-sup-sens"/>
          <Input placeholder={lang === "ar" ? "احتياجات حركية/جسدية — اختياري" : "Mobility / physical needs — optional"} value={form.support_profile.mobility_needs} onChange={e=>setSup("mobility_needs",e.target.value)} data-testid="f-sup-mob"/>
          <Input placeholder={lang === "ar" ? "ملاحظات معرفية / أكاديمية — اختياري" : "Cognitive / academic notes — optional"} value={form.support_profile.cognitive_notes} onChange={e=>setSup("cognitive_notes",e.target.value)} data-testid="f-sup-cog" className="md:col-span-2"/>

          <div className="md:col-span-2 flex gap-3 mt-2">
            <Btn type="submit" disabled={busy} data-testid="f-submit">{busy ? (lang === "ar" ? "جارٍ التكييف بـ GPT-5.2..." : "Adapting with GPT-5.2...") : (lang === "ar" ? "توليد التكييف الشامل وحفظ الهدف" : "Generate inclusive adaptation & save goal")}</Btn>
            <Btn variant="outline" type="button" onClick={()=>setShow(false)}>{lang === "ar" ? "إلغاء" : "Cancel"}</Btn>
          </div>
        </form>
      </Card>}

      {items.length === 0 ? <Card><p className="text-center text-[#4A5553] py-6">{lang === "ar" ? "لا توجد أهداف بعد. ابدأ بإنشاء هدف جديد." : "No goals yet. Start by creating a new goal."}</p></Card> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{items.map(g =>
          <Link key={g.id} to={`/curriculum/${g.id}`} data-testid={`goal-card-${g.id}`}>
            <Card className="hover:border-[#2F5233] transition cursor-pointer">
              <div className="flex justify-between items-start gap-2">
                <h3 className="font-semibold flex-1">{g.lesson_title}</h3>
                <StatusBadge status={g.status} data-testid={`status-${g.id}`}/>
              </div>
              <p className="text-xs text-[#4A5553] mt-1">{g.subject} · {g.grade_or_stage} · {g.curriculum_name}</p>
              <p className="text-sm mt-3 text-[#1A201E] line-clamp-2">{g.adapted_objective}</p>
              <p className="text-xs text-[#4A5553] mt-3">آخر تحديث: {fmtDate(g.updated_at)}</p>
            </Card>
          </Link>
        )}</div>}
    </div>
  );
}

function CurriculumGoalDetail() {
  const { lang } = useT();
  const gid = window.location.pathname.split("/").pop();
  const [g, setG] = useState(null);
  const [progressForm, setProgressForm] = useState({
    performance_notes: "", support_level: "partial", independence_status: "prompted",
    review_notes: "", score: 50,
  });
  const [busy, setBusy] = useState(false);
  const [cert, setCert] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/adaptation/goals/${gid}`);
    setG(data);
    if (data.certificate_id) {
      try { const { data: c } = await axios.get(`${API}/adaptation/goals/${gid}/certificate`); setCert(c); } catch {}
    }
  };
  useEffect(() => { load(); }, [gid]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (newStatus) => {
    try { await axios.patch(`${API}/adaptation/goals/${gid}/status`, { status: newStatus }); await load(); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); }
  };
  const addProgress = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post(`${API}/adaptation/goals/${gid}/progress`, progressForm);
      setProgressForm({ performance_notes:"", support_level:"partial", independence_status:"prompted", review_notes:"", score:50 });
      await load();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const closeGoal = async () => {
    if (!window.confirm("سيتم إغلاق الهدف نهائياً وإصدار شهادة. هل تريد المتابعة؟")) return;
    try { const { data } = await axios.post(`${API}/adaptation/goals/${gid}/close`); setCert(data); await load(); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); }
  };

  if (!g) return <div>...</div>;
  const closed = g.status === "closed";
  const avg = g.progress_history?.length ? Math.round(g.progress_history.reduce((s,p)=>s+(p.score||0),0)/g.progress_history.length) : 0;

  return (
    <div className="space-y-6 fade-up" data-testid="goal-detail">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <Link to="/curriculum" className="text-sm text-[#4A5553] hover:text-[#2F5233] inline-flex items-center gap-1"><ArrowRight size={14}/>العودة للقائمة</Link>
          <h1 className="text-3xl font-bold font-heading mt-2">{g.lesson_title}</h1>
          <p className="text-sm text-[#4A5553] mt-1">{g.subject} · {g.grade_or_stage} · {g.curriculum_name} · {g.unit_or_section}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={g.status}/>
          <div className="flex gap-2 flex-wrap justify-end">
            <Btn variant="outline" data-testid="share-goal-btn" onClick={()=>setShareOpen(true)}><Share2 size={14} className="inline ms-1"/>{lang==="ar"?"مشاركة":"Share"}</Btn>
            <Btn variant="outline" data-testid="print-goal-pdf" onClick={()=>printCurriculumGoal(g, g.progress_history||[], cert)}><Printer size={14} className="inline ms-1"/>{lang==="ar"?"طباعة":"Print"}</Btn>
            <Btn variant="primary" data-testid="export-goal-pdf" onClick={()=>downloadCurriculumGoal(g, g.progress_history||[], cert)}><FileDown size={14} className="inline ms-1"/>{lang==="ar"?"تنزيل PDF فاخر":"Premium PDF"}</Btn>
          </div>
        </div>
      </div>
      {shareOpen && <ShareGoalModal goalId={gid} lessonTitle={g.lesson_title} onClose={()=>setShareOpen(false)}/>}

      <div id="goal-report-area" className="space-y-6">
        <Card>
          <h2 className="text-lg font-semibold mb-2 font-heading">الهدف الأصلي</h2>
          <p className="text-[#4A5553] leading-relaxed bg-[#F0EFEB] p-3 rounded-lg">{g.original_objective}</p>
          <h2 className="text-lg font-semibold mt-4 mb-2 font-heading">الهدف بعد التكييف</h2>
          <p className="text-[#1A201E] leading-relaxed bg-[#2F5233]/5 border-r-4 border-[#2F5233] p-3 rounded-lg">{g.adapted_objective}</p>
          <div className="grid md:grid-cols-2 gap-3 mt-4 text-sm">
            <div><strong>الهدف الأكاديمي المحلل:</strong> <span className="text-[#4A5553]">{g.parsed_academic_target}</span></div>
            <div><strong>طريقة التقييم:</strong> <span className="text-[#4A5553]">{g.assessment_method}</span></div>
            <div><strong>معيار النجاح:</strong> <span className="text-[#4A5553]">{g.success_criteria}</span></div>
            <div><strong>فرع المادة:</strong> <span className="text-[#4A5553]">{g.subject_branch}</span></div>
            <div><strong>تكييف المرحلة:</strong> <span className="text-[#4A5553]">{g.grade_branch}</span></div>
            <div><strong>مسار الوصول:</strong> <span className="text-[#4A5553]">{g.access_branch}</span></div>
          </div>
        </Card>

        {g.lesson_plan?.length > 0 && <Card>
          <h2 className="text-lg font-semibold mb-3 font-heading">خطة الدرس المكيّفة</h2>
          <div className="space-y-2">{g.lesson_plan.map((s,i) =>
            <div key={i} className="flex gap-3 p-3 bg-[#F9F8F6] rounded-lg">
              <div className="bg-[#2F5233] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0">{s.step||i+1}</div>
              <div className="flex-1">
                <p className="font-semibold text-sm">{s.title}</p>
                <p className="text-sm text-[#4A5553] mt-1">{s.activity}</p>
                {s.duration_min && <p className="text-xs text-[#4A5553] mt-1">المدة: {s.duration_min} دقيقة</p>}
              </div>
            </div>)}</div>
        </Card>}

        {(g.support_strategies?.length > 0 || g.autism_supports?.length > 0) && <Card>
          <h2 className="text-lg font-semibold mb-3 font-heading">{lang === "ar" ? "الاستراتيجيات والدعائم المستخدمة" : "Support strategies used"}</h2>
          <div className="flex flex-wrap gap-2">{(g.support_strategies?.length ? g.support_strategies : g.autism_supports).map((s,i) => <span key={i} className="px-3 py-1.5 bg-[#769FCD]/15 text-[#527598] rounded-full text-sm">{s}</span>)}</div>
        </Card>}

        {g.accommodations?.length > 0 && <Card>
          <h2 className="text-lg font-semibold mb-3 font-heading">{lang === "ar" ? "التعديلات والتكييفات (Accommodations)" : "Accommodations"}</h2>
          <ul className="list-disc ps-5 text-sm text-[#4A5553] space-y-1">{g.accommodations.map((a,i)=><li key={i}>{a}</li>)}</ul>
        </Card>}

        {g.differentiation && (g.differentiation.content || g.differentiation.process || g.differentiation.product) && <Card>
          <h2 className="text-lg font-semibold mb-3 font-heading">{lang === "ar" ? "التمايز التربوي (Differentiation)" : "Differentiation"}</h2>
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            {g.differentiation.content && <div className="p-3 bg-[#F9F8F6] rounded-lg"><strong className="text-[#2F5233]">{lang==="ar"?"المحتوى":"Content"}</strong><p className="text-[#4A5553] mt-1">{g.differentiation.content}</p></div>}
            {g.differentiation.process && <div className="p-3 bg-[#F9F8F6] rounded-lg"><strong className="text-[#2F5233]">{lang==="ar"?"الأسلوب":"Process"}</strong><p className="text-[#4A5553] mt-1">{g.differentiation.process}</p></div>}
            {g.differentiation.product && <div className="p-3 bg-[#F9F8F6] rounded-lg"><strong className="text-[#2F5233]">{lang==="ar"?"الناتج":"Product"}</strong><p className="text-[#4A5553] mt-1">{g.differentiation.product}</p></div>}
          </div>
        </Card>}

        {g.assistive_tech?.length > 0 && <Card>
          <h2 className="text-lg font-semibold mb-3 font-heading">{lang === "ar" ? "التكنولوجيا المساندة" : "Assistive technology"}</h2>
          <div className="flex flex-wrap gap-2">{g.assistive_tech.map((a,i) => <span key={i} className="px-3 py-1.5 bg-[#E8A317]/15 text-[#8a5b00] rounded-full text-sm">{a}</span>)}</div>
        </Card>}
      </div>

      {!closed && <Card data-testid="status-controls">
        <h2 className="text-lg font-semibold mb-3 font-heading">إدارة دورة الحياة</h2>
        <div className="flex flex-wrap gap-2">
          {["active","in_progress","under_review","achieved","deferred"].map(s =>
            <button key={s} data-testid={`set-status-${s}`} onClick={()=>changeStatus(s)} disabled={g.status===s}
              className={`px-3 py-2 rounded-lg text-sm transition border ${g.status===s?"bg-[#2F5233] text-white border-[#2F5233]":"bg-white border-[#E5E2DB] hover:bg-[#F0EFEB]"}`}>
              {STATUS_LABELS[s].ar}
            </button>)}
        </div>
      </Card>}

      <Card data-testid="progress-section">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold font-heading flex items-center gap-2"><ClipboardList size={20}/>سجل التقدم ({g.progress_history?.length||0})</h2>
          {g.progress_history?.length > 0 && <div className="text-sm">متوسط الإتقان: <strong className="text-[#2F5233]">{avg}%</strong></div>}
        </div>

        {!closed && <form onSubmit={addProgress} className="mb-4 space-y-3 p-4 bg-[#F9F8F6] rounded-lg" data-testid="progress-form">
          <textarea placeholder="ملاحظات الأداء في الجلسة" value={progressForm.performance_notes} onChange={e=>setProgressForm({...progressForm,performance_notes:e.target.value})} required data-testid="p-notes"
            className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none min-h-[60px]"/>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><label className="block text-xs mb-1">مستوى الدعم</label>
              <select data-testid="p-support" value={progressForm.support_level} onChange={e=>setProgressForm({...progressForm,support_level:e.target.value})} className="w-full px-2 py-2 rounded-lg border border-[#E5E2DB] bg-white text-sm">
                <option value="full">دعم كامل</option><option value="partial">جزئي</option><option value="minimal">قليل</option><option value="independent">مستقل</option></select></div>
            <div><label className="block text-xs mb-1">الاستقلالية</label>
              <select data-testid="p-indep" value={progressForm.independence_status} onChange={e=>setProgressForm({...progressForm,independence_status:e.target.value})} className="w-full px-2 py-2 rounded-lg border border-[#E5E2DB] bg-white text-sm">
                <option value="prompted">بحثّ/تلقين</option><option value="independent">مستقل</option></select></div>
            <div><label className="block text-xs mb-1">نسبة الإتقان %</label>
              <input data-testid="p-score" type="number" min="0" max="100" value={progressForm.score} onChange={e=>setProgressForm({...progressForm,score:parseInt(e.target.value)||0})} className="w-full px-3 py-2 rounded-lg border border-[#E5E2DB] bg-white text-sm"/></div>
            <div className="flex items-end"><Btn type="submit" disabled={busy} data-testid="p-add" className="w-full">{busy?"...":"إضافة"}</Btn></div>
          </div>
          <Input placeholder="ملاحظات المراجعة (اختياري)" value={progressForm.review_notes} onChange={e=>setProgressForm({...progressForm,review_notes:e.target.value})} data-testid="p-review"/>
        </form>}

        {g.progress_history?.length > 0 ? <div className="space-y-2">{g.progress_history.map(p =>
          <div key={p.id} className="p-3 border border-[#E5E2DB] rounded-lg" data-testid={`progress-entry-${p.id}`}>
            <div className="flex justify-between items-start gap-2"><p className="text-sm font-medium">{p.performance_notes}</p>
              <span className="px-2 py-0.5 rounded text-xs bg-[#2F5233]/10 text-[#2F5233] flex-shrink-0">{p.score}%</span></div>
            <div className="flex flex-wrap gap-3 text-xs text-[#4A5553] mt-2">
              <span>دعم: {p.support_level}</span>
              <span>الاستقلالية: {p.independence_status}</span>
              <span>{fmtDate(p.ts)}</span>
            </div>
            {p.review_notes && <p className="text-xs text-[#4A5553] mt-1 italic">مراجعة: {p.review_notes}</p>}
          </div>
        )}</div> : !closed && <p className="text-sm text-[#4A5553] text-center py-3">لا توجد تسجيلات بعد</p>}
      </Card>

      {!closed && (g.status === "achieved" || avg >= 80) && <Card className="border-2 border-[#3A7D44] bg-[#3A7D44]/5">
        <h2 className="text-lg font-semibold mb-2 font-heading flex items-center gap-2"><CheckCircle2 className="text-[#3A7D44]"/>جاهز للإغلاق</h2>
        <p className="text-sm text-[#4A5553] mb-3">الهدف مؤهل للإغلاق الرسمي وإصدار شهادة الإنجاز.</p>
        <Btn data-testid="close-goal-btn" onClick={closeGoal}><Award size={16} className="inline ms-1"/>إغلاق الهدف وإصدار الشهادة</Btn>
      </Card>}

      {cert && <Card id="cert-area" className="border-2 border-[#2F5233] text-center" data-testid="goal-certificate">
        <Award className="mx-auto text-[#E8A317]" size={48}/>
        <h2 className="text-2xl font-bold mt-3 font-heading">{lang==="ar"?"شهادة إتقان الهدف":"Goal Mastery Certificate"}</h2>
        <p className="mt-4 text-[#4A5553]">{lang==="ar"?"تشهد منصة Inclusion+AI بإتقان":"Inclusion+AI certifies mastery of"}</p>
        <p className="text-2xl font-bold text-[#2F5233] my-2">{cert.lesson_title}</p>
        <p className="text-sm text-[#4A5553]">{lang==="ar"?"الهدف الأصلي":"Original objective"}: {cert.original_objective}</p>
        <p className="text-sm text-[#1A201E] mt-1"><strong>{lang==="ar"?"الهدف المكيّف":"Adapted objective"}:</strong> {cert.adapted_objective}</p>
        <p className="text-sm mt-3">{lang==="ar"?"نسبة الإتقان":"Mastery"}: <strong>{cert.mastery_score}%</strong></p>
        <p className="text-xs text-[#4A5553] mt-3">{lang==="ar"?"المتخصص":"Specialist"}: {cert.therapist} · {lang==="ar"?"رقم الشهادة":"Certificate #"}: {cert.id}</p>
        <p className="text-xs text-[#4A5553] mt-1">{lang==="ar"?"تاريخ الإصدار":"Issued at"}: {fmtDate(cert.issued_at)}</p>
        <div className="mt-4 flex gap-2 justify-center flex-wrap">
          <Btn variant="outline" data-testid="print-goal-cert" onClick={()=>printGoalCertificate(cert)}><Printer size={14} className="inline ms-1"/>{lang==="ar"?"طباعة الشهادة":"Print certificate"}</Btn>
          <Btn variant="primary" data-testid="download-goal-cert" onClick={()=>downloadGoalCertificate(cert)}><FileDown size={14} className="inline ms-1"/>{lang==="ar"?"تنزيل PDF فاخر":"Premium PDF"}</Btn>
        </div>
      </Card>}

      {closed && <Card className="bg-[#F0EFEB] text-center" data-testid="closure-info">
        <p className="text-sm"><strong>الهدف مغلق رسمياً.</strong></p>
        <p className="text-xs text-[#4A5553] mt-1">إنجاز: {fmtDate(g.achieved_at)} · إغلاق: {fmtDate(g.closed_at)}</p>
      </Card>}
    </div>
  );
}

// ============= SHARED PLAN VIEW (public, no auth) =============
// Goal share modal
function ShareGoalModal({ goalId, lessonTitle, onClose }) {
  const [days, setDays] = useState(7);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/share/goal`, { goal_id: goalId, days_valid: days });
      setLink({ url: `${window.location.origin}/shared-goal/${data.token}`, expires_at: data.expires_at });
    } catch (e) { alert(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const copy = async () => { await navigator.clipboard.writeText(link.url); setCopied(true); setTimeout(()=>setCopied(false),2000); };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose} data-testid="share-goal-modal">
      <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={e=>e.stopPropagation()}>
        <h3 className="text-xl font-bold font-heading mb-2">مشاركة هدف "{lessonTitle}"</h3>
        <p className="text-sm text-[#4A5553] mb-4">رابط للقراءة فقط يحتوي على الهدف، التقدم، والشهادة (إن وُجدت).</p>
        {!link ? <>
          <label className="block text-sm mb-2">صلاحية الرابط (أيام)</label>
          <Input type="number" min={1} max={30} value={days} onChange={e=>setDays(parseInt(e.target.value)||7)} data-testid="sg-days"/>
          <div className="flex gap-2 mt-5"><Btn onClick={generate} disabled={busy} data-testid="sg-generate">{busy?"...":"توليد الرابط"}</Btn><Btn variant="outline" onClick={onClose}>إلغاء</Btn></div>
        </> : <>
          <div className="bg-[#F0EFEB] p-3 rounded-lg text-sm break-all mb-3" data-testid="sg-link">{link.url}</div>
          <p className="text-xs text-[#4A5553] mb-4">صالح حتى: {fmtDate(link.expires_at)}</p>
          <div className="flex gap-2"><Btn onClick={copy} data-testid="sg-copy">{copied?<><Check size={16} className="inline ms-1"/>تم النسخ</>:<><Copy size={16} className="inline ms-1"/>نسخ</>}</Btn><Btn variant="outline" onClick={onClose}>إغلاق</Btn></div>
        </>}
      </div>
    </div>
  );
}

// Public goal view
function SharedGoal() {
  const token = window.location.pathname.split("/").pop();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/share/goal/${token}`, { withCredentials: false })
      .then(r => setData(r.data))
      .catch(e => setErr(fmtErr(e.response?.data?.detail) || "تعذر التحميل"));
  }, [token]);
  if (err) return <div className="min-h-screen flex items-center justify-center p-6"><Card><h2 className="text-xl font-bold text-[#D9534F]">{err}</h2></Card></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#4A5553]">جاري التحميل...</div>;
  const { goal: g, progress, certificate, shared_by, expires_at } = data;
  return (
    <div className="min-h-screen p-6 md:p-10" style={{background:"#F9F8F6"}} dir="rtl" data-testid="shared-goal">
      <div className="max-w-4xl mx-auto space-y-6 fade-up">
        <div className="flex items-center gap-3"><Brain className="text-[#2F5233]" size={32}/><h1 className="text-3xl font-bold text-[#2F5233] font-heading">Inclusion+AI</h1></div>
        <Card className="bg-[#769FCD]/10 border-[#769FCD]/30">
          <p className="text-sm"><strong>مشاركة من:</strong> {shared_by} · <strong>صالحة حتى:</strong> {fmtDate(expires_at)}</p>
          <p className="text-xs text-[#4A5553] mt-1">نسخة للقراءة فقط من هدف تكييف منهج.</p>
        </Card>
        <div className="flex justify-end gap-2">
          <Btn variant="outline" onClick={()=>printCurriculumGoal(g, progress||[], certificate)}><Printer size={14} className="inline ms-1"/>طباعة</Btn>
          <Btn variant="primary" onClick={()=>downloadCurriculumGoal(g, progress||[], certificate)}><FileDown size={14} className="inline ms-1"/>تنزيل PDF فاخر</Btn>
        </div>
        <div id="shared-goal-area" className="space-y-6 bg-white p-2 rounded-xl">
          <Card>
            <div className="flex justify-between items-start gap-2 mb-3"><h2 className="text-xl font-semibold font-heading">{g.lesson_title}</h2><StatusBadge status={g.status}/></div>
            <p className="text-xs text-[#4A5553]">{g.subject} · {g.grade_or_stage} · {g.curriculum_name} · {g.unit_or_section}</p>
            <h3 className="font-semibold mt-4 mb-1">الهدف الأصلي</h3>
            <p className="text-[#4A5553] bg-[#F0EFEB] p-3 rounded-lg">{g.original_objective}</p>
            <h3 className="font-semibold mt-3 mb-1">الهدف بعد التكييف</h3>
            <p className="bg-[#2F5233]/5 border-r-4 border-[#2F5233] p-3 rounded-lg">{g.adapted_objective}</p>
            <div className="text-sm mt-3 grid md:grid-cols-2 gap-2 text-[#4A5553]">
              <div><strong>التقييم:</strong> {g.assessment_method}</div>
              <div><strong>معيار النجاح:</strong> {g.success_criteria}</div>
            </div>
          </Card>
          {g.lesson_plan?.length > 0 && <Card>
            <h3 className="font-semibold mb-3 font-heading">خطة الدرس</h3>
            <div className="space-y-2">{g.lesson_plan.map((s,i) => <div key={i} className="flex gap-3 p-3 bg-[#F9F8F6] rounded-lg">
              <div className="bg-[#2F5233] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0">{s.step||i+1}</div>
              <div><p className="font-semibold text-sm">{s.title}</p><p className="text-sm text-[#4A5553] mt-1">{s.activity}</p></div>
            </div>)}</div>
          </Card>}
          {progress?.length > 0 && <Card>
            <h3 className="font-semibold mb-3 font-heading">سجل التقدم ({progress.length})</h3>
            <div className="space-y-2">{progress.map((p,i) => <div key={i} className="p-3 border border-[#E5E2DB] rounded-lg">
              <div className="flex justify-between"><span className="text-sm">{p.performance_notes}</span><span className="text-xs px-2 py-0.5 bg-[#2F5233]/10 text-[#2F5233] rounded">{p.score}%</span></div>
              <p className="text-xs text-[#4A5553] mt-1">{fmtDate(p.ts)} · دعم: {p.support_level} · {p.independence_status}</p>
            </div>)}</div>
          </Card>}
          {certificate && <Card className="border-2 border-[#2F5233] text-center">
            <Award className="mx-auto text-[#E8A317]" size={48}/>
            <h3 className="text-2xl font-bold mt-3 font-heading">شهادة إتقان</h3>
            <p className="mt-2">{certificate.lesson_title}</p>
            <p className="text-sm text-[#4A5553] mt-1">نسبة الإتقان: <strong>{certificate.mastery_score}%</strong></p>
            <p className="text-xs text-[#4A5553] mt-2">{certificate.id} · {fmtDate(certificate.issued_at)}</p>
          </Card>}
        </div>
      </div>
    </div>
  );
}

// ============= ANALYTICS / COMPARISON DASHBOARD =============
function Analytics() {
  const { t, lang } = useT();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/analytics/comparison`).then(r => setData(r.data)).finally(()=>setLoading(false));
  }, []);
  if (loading) return <div className="p-6 text-[#4A5553]">{t("status.loading")}</div>;
  const progressKey = lang === "ar" ? "تقدم" : "Progress";
  const completedKey = lang === "ar" ? "مكتمل" : "Completed";
  const screensKey = lang === "ar" ? "فحوصات" : "Screenings";
  const chartData = data.map(c => ({ name: c.name, [progressKey]: c.avg_progress, [completedKey]: c.completed_goals, [screensKey]: c.screenings_count }));
  return (
    <div className="space-y-6 fade-up" data-testid="analytics-page">
      <div><h1 className="text-4xl font-bold font-heading">{t("page.analytics")}</h1>
        <p className="text-[#4A5553] mt-1">{lang === "ar" ? "مقارنة تقدم الطلاب من ذوي الاحتياجات عبر الأهداف، الفحوصات، وتكييف المناهج." : "Compare progress of learners with diverse needs across goals, screenings, and curriculum adaptations."}</p></div>
      {data.length === 0 ? <Card><p className="text-center text-[#4A5553] py-6">{lang === "ar" ? "لا توجد بيانات بعد. أضف أطفالاً وابدأ التقييم." : "No data yet. Add children and start assessment."}</p></Card> : <>
      <Card data-testid="comparison-chart">
        <h2 className="text-xl font-semibold mb-4 font-heading">{lang === "ar" ? "متوسط التقدم لكل طفل" : "Average progress per child"}</h2>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} margin={{top:10,right:10,left:10,bottom:30}}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DB"/>
            <XAxis dataKey="name" tick={{fontSize:11,fill:"#4A5553"}}/>
            <YAxis domain={[0,100]} orientation="right" tick={{fontSize:11,fill:"#4A5553"}}/>
            <Tooltip contentStyle={{background:"#fff",border:"1px solid #E5E2DB",borderRadius:"8px",direction: lang === "ar" ? "rtl" : "ltr"}}/>
            <Bar dataKey={progressKey} fill="#2F5233" radius={[6,6,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card>
        <h2 className="text-xl font-semibold mb-4 font-heading">{lang === "ar" ? "الجدول التفصيلي" : "Detailed table"}</h2>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="text-[#4A5553] border-b border-[#E5E2DB]"><tr>
            <th className="text-start p-3">{lang === "ar" ? "الطفل" : "Child"}</th><th className="p-3">{lang === "ar" ? "العمر" : "Age"}</th><th className="p-3">{lang === "ar" ? "خطة" : "Plan"}</th>
            <th className="p-3">{lang === "ar" ? "إجمالي الأهداف" : "Total goals"}</th><th className="p-3">{lang === "ar" ? "مكتمل" : "Completed"}</th><th className="p-3">{lang === "ar" ? "متوسط" : "Average"}</th>
            <th className="p-3">{lang === "ar" ? "فحوصات" : "Screenings"}</th><th className="p-3">{lang === "ar" ? "أهداف مناهج" : "Curriculum goals"}</th>
          </tr></thead>
          <tbody>{data.map(c => <tr key={c.child_id} className="border-b border-[#E5E2DB] hover:bg-[#F9F8F6]" data-testid={`row-${c.child_id}`}>
            <td className="p-3 font-medium"><Link to={`/children/${c.child_id}`} className="text-[#2F5233] hover:underline">{c.name}</Link></td>
            <td className="p-3 text-center">{c.age}</td>
            <td className="p-3 text-center">{c.has_plan?"✓":"—"}</td>
            <td className="p-3 text-center">{c.total_goals}</td>
            <td className="p-3 text-center text-[#3A7D44] font-medium">{c.completed_goals}</td>
            <td className="p-3 text-center"><div className="inline-block w-20"><div className="bg-[#F0EFEB] h-2 rounded-full"><div className="bg-[#2F5233] h-2 rounded-full" style={{width:`${c.avg_progress}%`}}/></div><span className="text-xs">{c.avg_progress}%</span></div></td>
            <td className="p-3 text-center">{c.screenings_count}</td>
            <td className="p-3 text-center">{c.adapt_goals_count}</td>
          </tr>)}</tbody>
        </table></div>
      </Card>
      </>}
    </div>
  );
}

// ============= REMINDERS =============
function Reminders() {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title:"", scheduled_at:"", recurrence:"none", notes:"" });
  const load = () => axios.get(`${API}/reminders`).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault();
    try {
      const iso = new Date(form.scheduled_at).toISOString();
      await axios.post(`${API}/reminders`, { ...form, scheduled_at: iso });
      setShow(false); setForm({ title:"", scheduled_at:"", recurrence:"none", notes:"" }); load();
    } catch (e) { alert(fmtErr(e.response?.data?.detail)); }
  };
  const toggle = async (r) => { await axios.patch(`${API}/reminders/${r.id}`, { active: !r.active }); load(); };
  const del = async (id) => { if (!window.confirm(lang === "ar" ? "حذف التذكير؟" : "Delete reminder?")) return; await axios.delete(`${API}/reminders/${id}`); load(); };
  const RECUR = { none: t("reminders.freq_once"), daily: t("reminders.freq_daily"), weekly: t("reminders.freq_weekly") };
  return (
    <div className="space-y-6 fade-up" data-testid="reminders-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div><h1 className="text-4xl font-bold font-heading">{t("reminders.title")}</h1><p className="text-[#4A5553] mt-1">{lang === "ar" ? "جدولة تذكيرات مرة واحدة أو متكررة." : "Schedule one-time or recurring reminders."}</p></div>
        <Btn data-testid="new-reminder-btn" onClick={()=>setShow(true)}><Plus className="inline ms-1" size={16}/>{t("reminders.add").replace("+ ", "")}</Btn>
      </div>
      {show && <Card><form onSubmit={create} className="grid md:grid-cols-2 gap-4" data-testid="reminder-form">
        <Input placeholder={t("reminders.placeholder_title")} value={form.title} onChange={e=>setForm({...form,title:e.target.value})} required data-testid="r-title" className="md:col-span-2"/>
        <div><label className="block text-sm mb-1">{t("reminders.placeholder_when")}</label>
          <input type="datetime-local" required data-testid="r-time" value={form.scheduled_at} onChange={e=>setForm({...form,scheduled_at:e.target.value})}
            className="w-full px-4 py-2.5 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none"/></div>
        <div><label className="block text-sm mb-1">{t("reminders.frequency")}</label>
          <select data-testid="r-recur" value={form.recurrence} onChange={e=>setForm({...form,recurrence:e.target.value})} className="w-full px-4 py-2.5 rounded-lg border border-[#E5E2DB] bg-white">
            <option value="none">{t("reminders.freq_once")}</option><option value="daily">{t("reminders.freq_daily")}</option><option value="weekly">{t("reminders.freq_weekly")}</option></select></div>
        <Input placeholder={lang === "ar" ? "ملاحظات (اختياري)" : "Notes (optional)"} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} className="md:col-span-2"/>
        <div className="md:col-span-2 flex gap-3"><Btn type="submit" data-testid="r-save">{t("common.save")}</Btn><Btn variant="outline" type="button" onClick={()=>setShow(false)}>{t("common.cancel")}</Btn></div>
      </form></Card>}
      {items.length === 0 ? <Card><p className="text-center text-[#4A5553] py-6">{t("reminders.empty")}</p></Card> :
        <div className="space-y-2">{items.map(r => <Card key={r.id} className={!r.active?"opacity-60":""} data-testid={`reminder-${r.id}`}>
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2"><h3 className="font-semibold">{r.title}</h3>
                <span className="text-xs px-2 py-0.5 bg-[#769FCD]/15 text-[#527598] rounded">{RECUR[r.recurrence]}</span></div>
              <p className="text-sm text-[#4A5553] mt-1">{fmtDate(r.scheduled_at)}</p>
              {r.notes && <p className="text-xs text-[#4A5553] mt-1">{r.notes}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={()=>toggle(r)} data-testid={`toggle-${r.id}`} className="p-2 hover:bg-[#F0EFEB] rounded" title={r.active?(lang==="ar"?"إيقاف":"Disable"):(lang==="ar"?"تفعيل":"Enable")}>{r.active?<Bell size={18}/>:<BellOff size={18} className="text-[#4A5553]"/>}</button>
              <button onClick={()=>del(r.id)} data-testid={`delete-${r.id}`} className="p-2 hover:bg-red-50 rounded text-[#D9534F]"><Trash2 size={18}/></button>
            </div>
          </div>
        </Card>)}</div>}
    </div>
  );
}

// ============= SHARED PLAN VIEW (public, no auth) =============
function SharedPlan() {
  const token = window.location.pathname.split("/").pop();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/share/plan/${token}`, { withCredentials: false })
      .then(r => setData(r.data))
      .catch(e => setErr(fmtErr(e.response?.data?.detail) || "تعذر تحميل الخطة"));
  }, [token]);
  if (err) return <div className="min-h-screen flex items-center justify-center p-6"><Card><h2 className="text-xl font-bold text-[#D9534F]">{err}</h2><p className="text-sm text-[#4A5553] mt-2">تحقق من الرابط أو اطلب رابطاً جديداً.</p></Card></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#4A5553]">جاري التحميل...</div>;
  const { plan, child_name, age, gender, shared_by, expires_at } = data;
  return (
    <div className="min-h-screen p-6 md:p-10" style={{background:"#F9F8F6"}} dir="rtl" data-testid="shared-plan">
      <div className="max-w-4xl mx-auto space-y-6 fade-up">
        <div className="flex items-center gap-3"><Brain className="text-[#2F5233]" size={32}/><h1 className="text-3xl font-bold text-[#2F5233] font-heading">Inclusion+AI</h1></div>
        <Card className="bg-[#769FCD]/10 border-[#769FCD]/30">
          <p className="text-sm"><strong>مشاركة من:</strong> {shared_by} · <strong>صالحة حتى:</strong> {fmtDate(expires_at)}</p>
          <p className="text-xs text-[#4A5553] mt-1">هذه نسخة للقراءة فقط من خطة تأهيلية.</p>
        </Card>
        <div className="flex justify-end">
          <Btn variant="outline" data-testid="shared-export-pdf" onClick={()=>downloadPlan({name: child_name}, plan)}><FileDown size={16} className="inline ms-1"/>تنزيل PDF فاخر</Btn>
        </div>
        <div id="shared-plan-area" className="space-y-6 bg-white p-2 rounded-xl">
          <Card>
            <h2 className="text-xl font-semibold mb-3 font-heading">ملخص الحالة - {child_name}</h2>
            <p className="text-xs text-[#4A5553] mb-3">{age} سنة · {gender}</p>
            <p className="text-[#4A5553] leading-relaxed">{plan.summary}</p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div><p className="font-semibold mb-2">نقاط القوة</p><ul className="list-disc ps-5 text-sm text-[#4A5553]">{plan.strengths?.map((s,i)=><li key={i}>{s}</li>)}</ul></div>
              <div><p className="font-semibold mb-2">التحديات</p><ul className="list-disc ps-5 text-sm text-[#4A5553]">{plan.challenges?.map((s,i)=><li key={i}>{s}</li>)}</ul></div>
            </div>
            {plan.weekly_schedule && <div className="mt-4"><p className="font-semibold mb-2">الجدول الأسبوعي</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">{plan.weekly_schedule.map((w,i)=><div key={i} className="p-2 bg-[#F0EFEB] rounded"><strong>{w.day}:</strong> {w.activity}</div>)}</div></div>}
            {plan.parent_tips && <div className="mt-4"><p className="font-semibold mb-2">نصائح للأهل</p><ul className="list-disc ps-5 text-sm text-[#4A5553]">{plan.parent_tips.map((s,i)=><li key={i}>{s}</li>)}</ul></div>}
          </Card>
          <Card>
            <h2 className="text-xl font-semibold mb-4 font-heading">الأهداف التأهيلية</h2>
            <div className="space-y-4">{plan.goals?.map(g => <div key={g.id} className="p-4 rounded-lg border border-[#E5E2DB]">
              <div className="flex justify-between items-start gap-3"><div><h3 className="font-semibold">{g.title}</h3>
                <p className="text-xs text-[#4A5553] mt-1">{g.domain} · {g.duration_weeks} أسابيع · {g.techniques?.join(", ")}</p></div>
                <span className={`px-3 py-1 rounded-full text-xs ${g.progress>=80?"bg-[#3A7D44]/10 text-[#3A7D44]":"bg-[#E8A317]/10 text-[#E8A317]"}`}>{g.progress||0}%</span></div>
              <p className="text-sm mt-2"><strong>المعزز:</strong> {g.reinforcement}</p>
              <p className="text-sm"><strong>معيار النجاح:</strong> {g.success_criteria}</p>
            </div>)}</div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ============= HOME ACTIVITIES (parent-facing v3.2) =============
function HomeActivities() {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(null);
  const [openIds, setOpenIds] = useState({});
  useEffect(() => { axios.get(`${API}/parent/home-activities`).then(r => {
    setData(r.data);
    if (r.data.by_child?.length) setTab(r.data.by_child[0].child_id);
  }); }, []);
  if (!data) return <div className="p-8 text-center text-[#4A5553]">{t("status.loading")}</div>;
  const activeChild = data.by_child.find(b => b.child_id === tab);
  const filteredItems = activeChild?.items || [];
  return <div className="space-y-5 fade-up" data-testid="home-activities-page">
    <div>
      <div className="flex items-center gap-2 text-xs text-[#E8A317] font-semibold tracking-wider uppercase mb-1"><BookOpen size={14}/>{t("ha.title")}</div>
      <h1 className="text-3xl font-bold font-heading">{lang === "ar" ? "أنشطة منزلية مرتبطة بأهداف أبنائك" : "Home activities linked to your children's goals"}</h1>
      <p className="text-sm text-[#4A5553] mt-1">{t("ha.subtitle")}</p>
    </div>
    {data.by_child.length === 0 ? <Card><p className="text-center py-8 text-[#4A5553]">{lang === "ar" ? "لا يوجد أطفال مسجّلون بعد." : "No children registered yet."} <Link to="/children" className="text-[#2F5233] underline">{lang === "ar" ? "أضف طفلك الأول" : "Add your first child"}</Link>.</p></Card> :
    <>
      <div className="flex gap-2 flex-wrap border-b border-[#E5E2DB] pb-2" data-testid="children-tabs">
        {data.by_child.map(c => <button key={c.child_id} onClick={()=>setTab(c.child_id)} data-testid={`ha-tab-${c.child_id}`}
          className={`px-4 py-2 rounded-t-lg text-sm transition ${tab===c.child_id?"bg-[#E8A317] text-white":"bg-white text-[#4A5553] hover:bg-[#F0EFEB]"}`}>
          {c.child_name} <span className="text-xs opacity-80">({c.items.length})</span>
        </button>)}
      </div>
      {filteredItems.length === 0 ? <Card data-testid="ha-empty">
        <div className="text-center py-10">
          <BookOpen className="mx-auto mb-3 text-[#E8A317]/50" size={40}/>
          <p className="font-medium mb-1">{lang === "ar" ? `لم تُولَّد أنشطة منزلية بعد لـ ${activeChild?.child_name}` : `No home activities generated yet for ${activeChild?.child_name}`}</p>
          <p className="text-xs text-[#4A5553]">{lang === "ar" ? "عند تفعيل الخطة وتوليد موارد الأهداف من Goal Hub، ستظهر الأنشطة هنا للتطبيق المنزلي." : "Once the plan is active and goal resources are generated from Goal Hub, activities will appear here for home practice."}</p>
        </div>
      </Card> : <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filteredItems.map(r => {
          const c = r.content || {};
          const isOpen = !!openIds[r.id];
          const kindMeta = {
            home_activity: { label: lang === "ar" ? "نشاط منزلي" : "Home activity", color: "bg-emerald-100 text-emerald-700", icon: "🏠" },
            story:         { label: lang === "ar" ? "قصة قصيرة" : "Short story",   color: "bg-[#769FCD]/15 text-[#527598]", icon: "📖" },
            flashcards:    { label: lang === "ar" ? "بطاقات تعليمية" : "Flashcards", color: "bg-[#E8A317]/15 text-[#A36F00]", icon: "🃏" },
          }[r.kind] || { label: r.kind, color: "bg-[#F0EFEB] text-[#4A5553]", icon: "•" };
          return <div key={r.id} className="rounded-xl border border-[#E5E2DB] bg-white overflow-hidden" data-testid={`ha-card-${r.id}`}>
            <button onClick={()=>setOpenIds(p=>({...p, [r.id]: !isOpen}))} className="w-full p-3 text-start hover:bg-[#F9F8F6] transition flex items-start gap-3">
              <div className="text-2xl flex-shrink-0">{kindMeta.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${kindMeta.color}`}>{kindMeta.label}</span>
                </div>
                <p className="font-semibold text-sm line-clamp-1">{c.title || c.theme || c.name || r.goal_title || (lang === "ar" ? "نشاط" : "Activity")}</p>
                <p className="text-[11px] text-[#4A5553] line-clamp-1 mt-0.5">{lang === "ar" ? "الهدف:" : "Goal:"} {r.goal_title || "—"}</p>
              </div>
              <span className="text-xs text-[#4A5553] flex-shrink-0">{isOpen?"▲":"▼"}</span>
            </button>
            {isOpen && <div className="px-4 pb-4 border-t border-[#E5E2DB] pt-3 bg-[#F9F8F6]/30">
              <ParentResourceItem r={r}/>
            </div>}
          </div>;
        })}
      </div>}
    </>}
  </div>;
}

// ============= CERTIFICATES GALLERY (parent-facing v3.2) =============
function CertificatesGallery() {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/parent/certificates`).then(r => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-center text-[#4A5553]">{t("status.loading")}</div>;
  return <div className="space-y-5 fade-up" data-testid="certificates-gallery">
    <div>
      <div className="flex items-center gap-2 text-xs text-emerald-600 font-semibold tracking-wider uppercase mb-1"><Award size={14}/>{lang === "ar" ? "إنجازات أبنائك" : "Your children's achievements"}</div>
      <h1 className="text-3xl font-bold font-heading">{t("cert.title")}</h1>
      <p className="text-sm text-[#4A5553] mt-1">{t("cert.subtitle")}</p>
    </div>
    {data.items.length === 0 ? <Card data-testid="cert-empty">
      <div className="text-center py-12">
        <Award className="mx-auto mb-3 text-emerald-400" size={48}/>
        <p className="font-medium mb-1">{lang === "ar" ? "لا توجد شهادات بعد" : "No certificates yet"}</p>
        <p className="text-xs text-[#4A5553]">{t("cert.empty")}</p>
      </div>
    </Card> : <>
      {data.by_child.filter(b => b.items.length > 0).map(grp => <div key={grp.child_id}>
        <h2 className="text-base font-semibold mb-2 flex items-center gap-2 text-[#2F5233]"><User size={14}/>{grp.child_name} <span className="text-xs font-normal text-[#4A5553]">({grp.items.length} {lang === "ar" ? "شهادة" : "certificate(s)"})</span></h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
          {grp.items.map(cert => <div key={cert.id} className="rounded-xl border-2 border-emerald-200 bg-gradient-to-tl from-emerald-50/50 to-white p-4" data-testid={`cert-${cert.id}`}>
            <div className="flex items-start justify-between mb-3">
              <Award size={28} className="text-emerald-600"/>
              <span className="text-[10px] font-mono text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">{cert.id?.slice(-8)}</span>
            </div>
            <p className="font-bold text-base text-emerald-800 mb-1">{cert.child_name}</p>
            <p className="text-sm text-[#1A201E] line-clamp-2 mb-2" title={cert.goal_title}>{cert.goal_title || (lang === "ar" ? "هدف منجَز" : "Achieved goal")}</p>
            {cert.mastery_pct !== undefined && <div className="mb-2">
              <div className="flex justify-between text-[10px] mb-0.5"><span>{lang === "ar" ? "الإتقان" : "Mastery"}</span><strong className="text-emerald-700">{cert.mastery_pct}%</strong></div>
              <div className="h-1.5 bg-[#F0EFEB] rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{width:`${cert.mastery_pct}%`}}/></div>
            </div>}
            {cert.issued_at && <p className="text-[10px] text-[#4A5553]">📅 {new Date(cert.issued_at).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US", { day:"numeric", month:"long", year:"numeric" })}</p>}
            {cert.specialist_name && <p className="text-[10px] text-[#4A5553] mt-0.5">✍️ {cert.specialist_name}</p>}
          </div>)}
        </div>
      </div>)}
    </>}
  </div>;
}

// ============= APP =============
export default function App() {
  // dir/lang now managed by LanguageProvider
  return (
    <LanguageProvider>
    <A11yProvider>
    <AuthProvider>
      <BrowserRouter>
        <main id="main-content" tabIndex={-1}>
        <Routes>
          <Route path="/login" element={<Login/>}/>
          <Route path="/register" element={<Register/>}/>
          <Route path="/access" element={<ParentAccess/>}/>
          <Route path="/access/view" element={<ParentView/>}/>
          <Route path="/shared/:token" element={<SharedPlan/>}/>
          <Route path="/" element={<Protected><Dashboard/></Protected>}/>
          <Route path="/children" element={<Protected><Children/></Protected>}/>
          <Route path="/children/:id" element={<Protected><ChildDetail/></Protected>}/>
          <Route path="/screening" element={<Protected><Screening/></Protected>}/>
          <Route path="/advisor" element={<Protected><Advisor/></Protected>}/>
          <Route path="/curriculum" element={<Protected><CurriculumList/></Protected>}/>
          <Route path="/curriculum/:gid" element={<Protected><CurriculumGoalDetail/></Protected>}/>
          <Route path="/analytics" element={<Protected><Analytics/></Protected>}/>
          <Route path="/reminders" element={<Protected><Reminders/></Protected>}/>
          <Route path="/games" element={<Protected><GamesLibrary/></Protected>}/>
          <Route path="/games/play/:code" element={<Protected><GamePlayer/></Protected>}/>
          <Route path="/cases" element={<Protected><CasesList/></Protected>}/>
          <Route path="/cases/:cid" element={<Protected><CaseDetail/></Protected>}/>
          <Route path="/cases/:cid/evaluations/:eid" element={<Protected><EvaluationDetail/></Protected>}/>
          <Route path="/home-activities" element={<Protected><HomeActivities/></Protected>}/>
          <Route path="/certificates" element={<Protected><CertificatesGallery/></Protected>}/>
          <Route path="/admin" element={<Protected><AdminPanel/></Protected>}/>
          <Route path="/team" element={<Protected><TeamPageWrapper/></Protected>}/>
          <Route path="/shared-goal/:token" element={<SharedGoal/>}/>
        </Routes>
        </main>
      </BrowserRouter>
    </AuthProvider>
    </A11yProvider>
    </LanguageProvider>
  );
}
